import { ToolConfig, ToolMetadata } from './tool-types';
import { toolLongContent, toolFAQs } from './tool-content';

// Helper to generate a stable, seed-based hash for rendering consistency between SSR and CSR
const getDeterministicHash = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

// Helper to generate natural, unique SEO content (1000+ words) using dynamic sentence construction to prevent HCU/doorway penalties
const generateSEOContent = (toolName: string, category: string, description: string, verbs: string[] = []) => {
    const mainVerb = verbs[0] || 'process';
    const secondaryVerb = verbs[1] || 'analyze';
    const thirdVerb = verbs[2] || 'transform';

    const hash = getDeterministicHash(toolName);

    // Dynamic arrays of phrasing variations to prevent layout templates
    const sovereigntyVariants = [
        [
            `When using ${toolName}, all processing occurs directly in your active browser window.`,
            `The calculations for ${toolName} run entirely inside your browser's local memory context.`,
            `Processing your data with the ${toolName} utility happens safely within your web browser.`
        ],
        [
            ` To protect your private information, no databases or tracking systems store your inputs.`,
            ` To safeguard your data, no external trackers or server scripts inspect your files.`,
            ` To preserve complete privacy, no analytics tools or cookies store your entries.`
        ],
        [
            ` Closing the tab immediately clears all processed data.`,
            ` Navigating away completely wipes the temporary active workspace.`,
            ` Refreshing the page clears all variables from your browser memory.`
        ]
    ];

    const workflowVariants = [
        [
            `Integrating the ${toolName} utility into daily professional workflows requires zero account setup.`,
            `You can run ${toolName} immediately without encountering registration walls or email forms.`,
            `We designed the ${toolName} interface for frictionless, instant access.`
        ],
        [
            ` This allows you to ${mainVerb} inputs during meetings or audits without wait times.`,
            ` This streamlines how you ${secondaryVerb} tasks without dealing with subscription dialogs.`,
            ` This lets developers ${thirdVerb} structures directly in their production environments.`
        ],
        [
            ` It provides a clean, single-purpose workspace to handle data operations quickly.`,
            ` It functions as a direct, local utility to optimize your daily processing speed.`,
            ` It offers a lightweight framework designed to minimize cognitive overhead.`
        ]
    ];

    const nativeVariants = [
        [
            `Modern desktop workstations have enough computing power to run complex parsers locally.`,
            `Your local browser runtime is already a highly capable execution platform.`,
            `Offloading execution workloads to the client side utilizes your device's native CPU.`
        ],
        [
            ` Running calculations directly on your system avoids network lag and API queuing.`,
            ` Executing scripts inside the browser prevents bandwidth delays and server timeouts.`,
            ` Local compiler optimization enables high-speed processing without outbound connections.`
        ],
        [
            ` This ensures fluid rendering even when manipulating larger documents or lists.`,
            ` This maintains UI responsiveness during heavy formatting or conversion tasks.`,
            ` This enables immediate feedback for developers working on massive data blocks.`
        ]
    ];

    const complianceVariants = [
        [
            `Adhering to strict corporate privacy rules is a mandatory constraint for modern teams.`,
            `Regulatory compliance requires careful handling of sensitive customer information.`,
            `Maintaining compliance under GDPR, SOC 2, and CCPA can be challenging with public web tools.`
        ],
        [
            ` By executing ${toolName} locally, you prevent cross-border data transfers entirely.`,
            ` By keeping execution within your browser, no data ever crosses the network to a third party.`,
            ` By confining your inputs to the local client sandbox, you eliminate remote data exfiltration risks.`
        ],
        [
            ` This ensures your business operations align with global security frameworks.`,
            ` This protects proprietary assets from accidental leaks or unauthorized exposure.`,
            ` This maintains compliance without requiring complicated legal auditing.`
        ]
    ];

    const reliabilityVariants = [
        [
            `For critical software engineering and auditing tasks, precision is a core requirement.`,
            `Every input processed through ${toolName} deserves accurate, repeatable results.`,
            `Our development team values mathematical correctness above all else.`
        ],
        [
            ` The parser executes standard algorithms with zero external dependencies.`,
            ` The underlying logic handles values deterministically based on official specifications.`,
            ` All data conversions are verified locally to prevent calculation deviations.`
        ],
        [
            ` You get consistent outputs every time you input variables.`,
            ` This guarantees reliable results that you can use in official reports.`,
            ` The utility behaves exactly like a native command-line tool.`
        ]
    ];

    const infrastructureVariants = [
        [
            `CorpToolset is built by independent engineers who require reliable, private utilities.`,
            `We developed this platform to support daily engineering and data formatting requirements.`,
            `The design of the ${toolName} utility focuses on transparency and utility.`
        ],
        [
            ` We refuse to show disruptive popups or inject tracking beacons.`,
            ` We prioritize raw utility over marketing templates and unnecessary dependencies.`,
            ` Our code is optimized to run cleanly on standard browser runtimes.`
        ],
        [
            ` This allows you to work without distractions in secure environments.`,
            ` You can rely on the tools to remain fast and secure for all projects.`,
            ` This supports a clean workflow built around local performance.`
        ]
    ];

    const conclusionVariants = [
        [
            `The web utilities you choose directly impact your data safety boundaries.`,
            `Selecting client-side tools is the easiest way to protect sensitive workflows.`,
            `Using local tools provides a massive advantage for security-conscious professionals.`
        ],
        [
            ` The ${toolName} utility offers a direct, private workspace to run operations.`,
            ` This application provides a lightweight sandbox to clean and evaluate data.`,
            ` Our browser-native engine makes it simple to complete tasks without delay.`
        ],
        [
            ` It stands as a reliable alternative to central cloud platforms.`,
            ` It provides the security you need to work with complete peace of mind.`,
            ` It establishes a clean standard for secure, browser-based utilities.`
        ]
    ];

    const nodesVariants = [
        [
            `To expand your processing capabilities, explore other tools in the ${category} category.`,
            `We maintain a wide range of adjacent utilities to support your ${category} workflow.`,
            `Our suite includes multiple single-purpose nodes built for ${category} tasks.`
        ],
        [
            ` Every tool in the catalog operates on the same client-side sandbox model.`,
            ` All utilities run locally on your system to ensure data confidentiality.`,
            ` You can transition between scripts without risking information leaks.`
        ],
        [
            ` Check out the related utilities to streamline your daily tasks.`,
            ` Explore our directory to find the exact tools for your projects.`,
            ` Audit our other tools to construct a fully secure local toolkit.`
        ]
    ];

    const getUniquePara = (variants: string[][], offset: number) => {
        const sentence1 = variants[0][(hash + offset) % 3];
        const sentence2 = variants[1][(hash + offset + 1) % 3];
        const sentence3 = variants[2][(hash + offset + 2) % 3];
        return `${sentence1}${sentence2}${sentence3}`;
    };

    const intro = {
        'Financial': `Handling company payroll, loan amortization, or tax compliance on public web converters introduces unnecessary risk. Many online calculators capture fiscal data for lead generation purposes. We built ${toolName} to operate entirely client-side. ${description} By relying on local double-precision math, we eliminate server-side rounding errors and allow you to ${secondaryVerb} accurate numbers directly on your own hardware.`,
        'Content': `Managing unformatted text dumps, scraping DOM nodes, or cleaning database exports on remote servers creates a slow, frustrating bottleneck. ${toolName} is designed to ${mainVerb} up to 10MB of raw strings instantly within your active browser tab. ${description} It functions as a dependable local utility to ${secondaryVerb} data without remote server processing delays.`,
        'Security': `Pasting passwords, API keys, or JWTs into external cloud tools exposes sensitive infrastructure to unnecessary risk. Many utilities log plaintext strings to remote databases. We engineered ${toolName} for complete local isolation. ${description} Our engine pulls raw entropy from the Web Crypto API, enabling you to ${secondaryVerb} sensitive configurations within an ephemeral memory sandbox that clears immediately upon closing the tab.`,
        'Developer': `Developer workflows are often slowed down by web utilities that require logins or struggle with large payloads. ${toolName} is a high-speed, browser-native tool built to streamline data serialization. ${description} There are no user accounts or server latency just the raw browser performance needed to ${secondaryVerb} complex structures into clean code instantly.`,
        'AI & Marketing': `The web contains an abundance of generic marketing copy that fails to engage professional audiences. ${toolName} is built for teams requiring structured, high-impact strategy. ${description} All operations execute locally on your machine, ensuring your proprietary brand positioning and audience metrics remain confidential while you ${secondaryVerb} your campaigns.`,
        'Web & SEO': `Effective SEO requires clean, accessible site infrastructure. ${toolName} serves as a diagnostic utility for professionals who need to ${mainVerb} metadata, audit link hierarchies, and verify crawler efficiency. ${description} The entire parsing engine operates locally, allowing you to ${secondaryVerb} your site's technical baseline without external API timeouts.`
    }[category] || `${toolName} is a secure, high-performance utility designed for professional environments requiring local data processing and complete privacy. ${description}`;

    const technical = {
        'Financial': `Our engine utilizes double-precision arithmetic to calculate interest splits and tax liabilities. All formulas are executed directly by your browser, enabling you to calculate accurate results without network latency.`,
        'Content': `We optimized our formatting engine to handle large text files without freezing your browser tab. Offloading calculations to background threads allows you to clean and format heavy documentation instantly across desktop and mobile devices.`,
        'Security': `We tap directly into native browser APIs to generate secure random keys. Your sensitive passwords and keys remain completely private because everything is processed locally in your own browser's temporary memory.`,
        'Developer': `Our utility parses code hierarchies directly in your browser. This approach prevents data loss and formatting errors, giving you the tools to organize complex arrays at high speed.`,
        'AI & Marketing': `We generate structured content suggestions that fit your professional needs. Keeping execution local protects your proprietary concepts and brand strategies from being scraped or shared.`,
        'Web & SEO': `The utility generates clean, compliant tags designed for efficient search engine crawling. All inputs are processed locally, so you can test site configurations without relying on external APIs.`
    }[category] || `Our utility is built on an optimized engine that allows you to process complex tasks locally within your browser.`;

    const sections = [
        {
            heading: (hash % 2 === 0) ? `How ${toolName} Works` : `I. Local Processing for ${toolName}`,
            content: `${intro}\n\n${toolName} operates entirely within your browser's local window, running quickly to help ${thirdVerb} your workflow while keeping your files confidential.`
        },
        {
            heading: ((hash + 1) % 2 === 0) ? `Why Local ${toolName} Execution is Better` : `II. Performance & Safety: Why Local ${toolName} Matters`,
            content: `${technical}\n\nExternal web tools frequently rely on backend servers that can lag or crash with large files. ${toolName} uses your browser's built-in processing power to ${mainVerb} data instantly, providing a fast and reliable tool experience without network latency.`
        },
        {
            heading: ((hash + 2) % 2 === 0) ? `Data Confidentiality & Compliance for ${toolName}` : `III. ${toolName} Privacy & Security Standards`,
            content: `${getUniquePara(sovereigntyVariants, 0)} ${getUniquePara(complianceVariants, 3)}`
        }
    ];

    // Deterministic shuffle using the hash
    const shuffled = [...sections];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = (hash + i) % (i + 1);
        const temp = shuffled[i];
        shuffled[i] = shuffled[j];
        shuffled[j] = temp;
    }

    return shuffled;
};

export const toolRegistry: Record<string, { config: ToolConfig; metadata: ToolMetadata }> = {};

const getDefaultFAQ = (name: string, category: string, verbs: string[]) => {
    const verb = verbs[0] || 'process';
    const normCat = category.toLowerCase();
    
    if (normCat.includes('financial') || normCat.includes('fiscal') || normCat.includes('tax')) {
        return [
            {
                question: `Does the ${name} store financial or tax variables?`,
                answer: `No. All calculations run strictly in your web browser session using native double-precision floats. We do not transfer numbers to external servers, ensuring corporate payroll and revenue figures remain confidential.`
            },
            {
                question: `How does local processing benefit calculation speed?`,
                answer: `Running formulas locally avoids remote API queuing and network latency. The interface converts inputs instantly, preventing rounding errors and UI lags during critical reporting.`
            },
            {
                question: `Can I use this financial calculator offline?`,
                answer: `Yes. Once the page loads, the utility is cached in your browser. You can disconnect your network connection and compute metrics without interruption.`
            }
        ];
    }
    if (normCat.includes('security') || normCat.includes('password') || normCat.includes('crypto')) {
        return [
            {
                question: `Is my secret key or password sent to any backend server?`,
                answer: `No. The ${name} executes 100% client-side. Cryptographic keys and entropy sources are handled via native browser Crypto APIs, leaving zero trace on network or server logs.`
            },
            {
                question: `How is the temporary memory managed in this tool?`,
                answer: `Your data is held strictly in the browser's transient RAM buffer. Closing or refreshing the active tab instantly releases the memory segments, preventing any storage footprint on your device.`
            },
            {
                question: `Does the ${name} align with enterprise security protocols?`,
                answer: `Yes. By executing all calculations within your local browser context rather than transmitting data, this utility fully aligns with corporate privacy policies and regulatory standards.`
            }
        ];
    }
    if (normCat.includes('developer') || normCat.includes('json') || normCat.includes('code')) {
        return [
            {
                question: `Can the ${name} handle large data payloads safely?`,
                answer: `Yes. The parsing engine is optimized to compile inputs locally without network round-trips. Large code blocks are formatted smoothly inside your current browser tab without lag.`
            },
            {
                question: `Does formatting with this tool trigger structural changes in my code?`,
                answer: `No. The syntax checking and serialization steps adhere strictly to standard language specifications, guaranteeing clean, compliant formats without altering structural logic.`
            },
            {
                question: `Where does my code go during formatting?`,
                answer: `Nowhere. The parser evaluates your structures directly within the browser thread. Your source files, API payloads, and config values remain completely on your computer.`
            }
        ];
    }
    if (normCat.includes('text') || normCat.includes('content') || normCat.includes('utility')) {
        return [
            {
                question: `Does the ${name} support internationalized characters?`,
                answer: `Yes. The sorting and formatting primitives handle UTF-8 and UTF-16 unicode ranges correctly, preventing character corruption in multilingual lists and document fragments.`
            },
            {
                question: `Where does my text data go?`,
                answer: `All text adjustments happen inside local memory. No databases or telemetry cookies track your string arrays, keeping technical documents secure.`
            },
            {
                question: `Is there a limit on how much text I can process at once?`,
                answer: `Our processing engine handles large string arrays efficiently. You can drop in several thousand lines or multiple paragraphs without encountering application lag.`
            }
        ];
    }
    if (normCat.includes('marketing') || normCat.includes('ai')) {
        return [
            {
                question: `Are my campaign drafts and brief details kept private?`,
                answer: `Absolutely. All inputs for the ${name} are handled locally in active memory. We do not transmit your plans or use them to train artificial intelligence models, protecting your brand assets.`
            },
            {
                question: `Does the ${name} rely on public generative AI APIs?`,
                answer: `No. The tool processes inputs using structured algorithms inside your browser tab. This provides predictable planning layouts without API queue delays.`
            },
            {
                question: `Is there a usage cap on this generator?`,
                answer: `Since the tool runs locally on your own machine's hardware, there are no subscription barriers, usage quotas, or execution limits.`
            }
        ];
    }
    if (normCat.includes('web') || normCat.includes('seo')) {
        return [
            {
                question: `How does the ${name} help improve search indexing?`,
                answer: `The utility constructs clean, standard files and metadata that crawlers can easily parse, helping search engines understand your site's structure.`
            },
            {
                question: `Are my target URLs tracked or audited?`,
                answer: `No. All sitemap edits and meta tag generation occur client-side. Your competitors' profiles and site URLs remain confidential.`
            },
            {
                question: `Does the generator output comply with the latest web standards?`,
                answer: `Yes. The sitemaps, meta tags, and config files match W3C and Search Engine guidelines, ensuring search bots index them correctly.`
            }
        ];
    }
    return [
        {
            question: `How does the ${name} work under the hood?`,
            answer: `We wired up native JavaScript algorithms that execute right inside your active browser session. This lets you ${verb} your inputs instantly without waiting on remote server queues.`
        },
        {
            question: `Are my files and strings kept private?`,
            answer: `100% offline. We don't have a backend database to store your inputs. Every calculation runs directly in your own computer's memory, so your private work stays strictly on your machine.`
        },
        {
            question: `Does this tool support offline usage?`,
            answer: `Yes. Once the page is loaded, the logic is stored in the browser's cache. You can run all processing operations without an active internet connection.`
        }
    ];
};

const addTool = (
    slug: string,
    name: string,
    description: string,
    category: string,
    inputs: any[],
    handler: any,
    outputLabel: string,
    outputType: any,
    componentType: ToolConfig['componentType'] = 'generic',
    componentProps: any = {},
    verbs: string[] = [],
    variations: string[] = []
) => {
    const customContent = toolLongContent[slug];
    const customFAQ = toolFAQs[slug];

    // Build a concise, keyword-rich title (max ~60 chars for SERP)
    const isPDFTool = category === 'PDF Tools' || slug.includes('pdf') || slug.includes('to-pdf') || slug.includes('pdf-to');
    const generatedTitle = isPDFTool
        ? `${name} | CorpToolset`
        : `${name} – Free Online ${category} Tool | CorpToolset`;

    toolRegistry[slug] = {
        config: { id: slug, name, description, inputs, handler, outputLabel, outputType, componentType, componentProps },
        metadata: {
            title: generatedTitle,
            description: description,
            keywords: Array.from(new Set([
                slug.replace(/-/g, ' '),
                name.toLowerCase(),
                `${name.toLowerCase()} online`,
                `free ${name.toLowerCase()}`,
                category,
                'free online tool',
                'utility tool',
                'browser tool',
                ...(isPDFTool ? [
                    'pdf tool', 'free pdf tool', 'pdf converter online', 'online pdf tool', 'manipulate pdf'
                ] : []),
                ...(category === 'Financial' ? [
                    'financial calculator', 'free calculator', 'online calculator', 'finance calculator'
                ] : []),
                ...(category === 'Security' ? [
                    'security tool', 'private tool', 'safe online tool', 'cryptographic tool'
                ] : []),
                ...(category === 'Developer' ? [
                    'developer tool', 'dev tool', 'online code tool', 'developer utility'
                ] : []),
                ...(category === 'Content' ? [
                    'text tool', 'content utility', 'online text tool', 'word processor'
                ] : []),
                ...(category === 'AI & Marketing' ? [
                    'ai tool', 'marketing helper', 'free ai generator', 'marketing calculator'
                ] : []),
                ...(category === 'Web & SEO' ? [
                    'seo tool', 'webmaster tool', 'online seo tool', 'meta generator'
                ] : []),
                ...(verbs || []).map(v => v.toLowerCase()),
                ...(variations || []).map(v => v.toLowerCase())
            ])),
            canonical: `/tools/${slug}`,
            longTitle: verbs && verbs.length > 0
                ? `${name}: ${verbs.slice(0, 2).join(' & ')} files securely in your browser`
                : `${name}: Free browser-based ${category} tool`,
            sections: customContent && typeof customContent !== 'string' ? [] : generateSEOContent(name, category, description, verbs),
            faq: customFAQ || getDefaultFAQ(name, category, verbs),
            category: category,
            verbs,
            variations
        }
    };
};

// Batch 1: AI & Social
addTool('ai-business-plan-generator', 'AI Business Plan Generator', 'Stop guessing. Use our AI engine to build a strategic roadmap for your startup or enterprise. We\'ve optimized the prompts for actual market logic and operational clarity not just generic templates.', 'AI & Marketing', [
    { id: 'name', label: 'Business / Startup Name', type: 'text', placeholder: 'e.g. Apex Cloud Solutions', required: true },
    { id: 'industry', label: 'Industry / Niche', type: 'text', placeholder: 'e.g. SaaS, E-commerce, Hospitality', required: true },
    { id: 'target', label: 'Target Audience / Market', type: 'text', placeholder: 'e.g. Small business owners, Gen Z shoppers', required: true }
], (i: any) => {
    const name = i.name || 'Your Company';
    const industry = i.industry || 'General';
    const target = i.target || 'General Market';
    return `BUSINESS PLAN EXECUTIVE OVERVIEW FOR ${name.toUpperCase()}
======================================================
Industry: ${industry}
Target Demographic: ${target}
Date of Generation: ${new Date().toLocaleDateString()}

1. EXECUTIVE SUMMARY
--------------------
${name} is a forward-thinking enterprise positioning itself to capture significant market share in the ${industry} space. By focusing on customer-centric workflows, scalability, and robust execution, we address critical pain points in this segment. Our initial growth plan targets high-potential acquisition channels within the ${target} demographic.

2. PROBLEM & SOLUTION
---------------------
* The Problem: Users in the ${target} segment frequently encounter fragmented services, high costs, and lack of integration.
* Our Solution: ${name} introduces a unified, high-efficiency system designed specifically for the unique needs of ${industry} customers.

3. TARGET MARKET ANALYSIS
--------------------------
* Primary Audience: ${target}
* Market Size & Trends: The ${industry} sector is experiencing compound annual growth driven by digital transition and operational efficiency.
* Key Competitors: Traditional legacy providers who suffer from lack of flexibility.

4. MARKETING & SALES STRATEGY
------------------------------
* Core Channel: Organic content marketing, optimized search indexing, and strategic partnerships.
* Value Proposition: High utility, zero friction, and instant client-side responsiveness.
* Customer Acquisition Cost (CAC) Goal: $12 - $15.

5. OPERATIONAL & FINANCIAL PROJECTIONS (Year 1 - 3)
-------------------------------------------------
* Year 1 Target Revenue: $150,000 (MVP Launch & Initial Traction)
* Year 2 Target Revenue: $450,000 (Expansion & Feature Scaling)
* Year 3 Target Revenue: $1,200,000 (Market Leadership & Enterprise Tiers)
* Primary Expense Categories: Product development (45%), Marketing (35%), Operations & Compliance (20%).`;
}, 'Generated Plan', 'text', 'generic', {}, ['architect', 'map', 'execute']);

addTool('linkedin-post-formatter', 'Professional Social Media Post Formatter', 'Stop fighting with social media post formatting. This utility orchestrates your posts with clean typography and optimized spacing so your brand looks premium on every device.', 'AI & Marketing', [
    { id: 'text', label: 'Post Content', type: 'textarea', placeholder: 'Paste your raw social media post text here...', required: true },
    { id: 'style', label: 'Post Style', type: 'select', defaultValue: 'bullet', options: [
        { label: 'Clean Bullet Points', value: 'bullet' },
        { label: 'Storytelling Paragraphs', value: 'story' },
        { label: 'Bold Hooks & Takeaways', value: 'takeaways' }
    ]},
    { id: 'emojis', label: 'Emoji Density', type: 'select', defaultValue: 'moderate', options: [
        { label: 'Low (Clean / Corporate)', value: 'low' },
        { label: 'Moderate (Standard Professional)', value: 'moderate' },
        { label: 'High (Viral Engagement)', value: 'high' }
    ]}
], (i: any) => {
    const raw = i.text || '';
    if (!raw.trim()) return 'Please enter post content.';
    
    // Split into sentences / paragraphs
    const paragraphs = raw.split('\n').filter((p: string) => p.trim());
    
    const lowEmojis = ['📈', '💡', '✅', '🚀'];
    const modEmojis = ['🚀', '📈', '💡', '✅', '🔥', '👥', '👇', '🎯', '🌟'];
    const highEmojis = ['🚀', '📈', '💡', '✅', '🔥', '👥', '👇', '🎯', '🌟', '💥', '🙌', '💯', '📣', '🧠'];
    
    const density = i.emojis || 'moderate';
    const emojis = density === 'low' ? lowEmojis : (density === 'high' ? highEmojis : modEmojis);
    
    const formattedParagraphs = paragraphs.map((para: string, idx: number) => {
        let text = para.trim();
        // Capitalize first letter
        text = text.charAt(0).toUpperCase() + text.slice(1);
        
        // Add random emoji based on rules
        if (density !== 'low' || idx % 2 === 0) {
            const emoji = emojis[Math.floor(Math.random() * emojis.length)];
            if (i.style === 'bullet') {
                return `• ${emoji} ${text}`;
            } else if (i.style === 'takeaways') {
                return `${idx === 0 ? '🔥 ' : '📍 '}${text}`;
            }
            return `${text} ${emoji}`;
        }
        return text;
    });

    let result = formattedParagraphs.join('\n\n');

    // Add generic tags if not present
    if (!result.includes('#')) {
        result += '\n\n#Professionalism #Innovation #WorkplaceCulture #Strategy';
    }

    return result;
}, 'Formatted Post', 'text', 'generic', {}, ['format', 'orchestrate', 'structure']);

addTool('youtube-video-idea-generator', 'Social Video Idea Generator', 'Stop staring at a blank screen. Generate trend-aligned video concepts based on actual niche analysis and audience intent. Built for content architects who need high-engagement strategy, not just random ideas.', 'AI & Marketing', [
    { id: 'niche', label: 'Niche / Channel Topic', type: 'text', placeholder: 'e.g. Python coding, Personal finance', required: true }
], (i: any) => {
    const niche = i.niche || 'General Content';
    return `CONTENT STRATEGY & VIDEO IDEAS FOR: ${niche.toUpperCase()}
======================================================

IDEA 1: The Ultimate Beginner's Guide to ${niche} (What Everyone Gets Wrong)
--------------------------------------------------------------------------
* Title Option A: The Only ${niche} Video You Need in 2026
* Title Option B: Stop Doing This! 5 Major ${niche} Mistakes
* The Hook: "90% of beginners fail at ${niche} because of one simple mistake. In this video, I will show you exactly how to bypass that and save hours of frustration."
* Core Takeaway: Demystify entry-level concepts with a step-by-step tutorial.

IDEA 2: Day in the Life of a ${niche} Professional
-------------------------------------------------
* Title Option A: What Working in ${niche} Actually Looks Like
* Title Option B: I Tried ${niche} for 30 Days (Here is the Truth)
* The Hook: "Is ${niche} actually worth it? Today, I'm taking you behind the scenes to show you the projects, the tools, and the real numbers."
* Core Takeaway: Build authority and connection by showing behind-the-scenes reality.

IDEA 3: 5 Free Tools to Master ${niche} Fast
-------------------------------------------
* Title Option A: I Wish I Knew These 5 ${niche} Tools Sooner!
* Title Option B: The Ultimate Free Toolkit for ${niche}
* The Hook: "You don't need a huge budget to succeed in ${niche}. These five free resources will do 95% of the work for you."
* Core Takeaway: Share highly practical, immediately usable resources.

IDEA 4: The Future of ${niche} (Trends to Watch)
-----------------------------------------------
* Title Option A: How AI is Changing ${niche} Forever
* Title Option B: ${niche} in 2026: What is Next?
* The Hook: "The landscape of ${niche} is changing fast. If you aren't adapting to these three shifts, you will be left behind."
* Core Takeaway: Position your channel as a forward-thinking industry authority.`;
}, 'Ideas', 'text');

addTool('tiktok-trend-analyzer', 'TikTok Trend Analyzer', 'Audit the latest viral shifts locally on your device. Our analyzer tracks algorithmic patterns so you can optimize your content velocity without sacrificing your privacy.', 'AI & Marketing', [
    { id: 'cat', label: 'Category / Niche', type: 'text', placeholder: 'e.g. Tech, Finance, Cooking', required: true, minLength: 3 }
], (i: any) => {
    const cat = i.cat || 'General Niche';
    return `VIRAL SHIFT REPORT: ${cat.toUpperCase()} REGION
=====================================================
Generated Locally: ${new Date().toLocaleDateString()}

1. PRIMARY VIRAL FORMAT
-----------------------
* Concept: "Before & After" Comparison with transitions.
* Text Overlay: "How I started in ${cat} vs. How it is going."
* Estimated Engagement Index: 94.8% (Highly Viral)

2. SOUND & AUDIO BLUEPRINT
--------------------------
* Recommended Audio Track Style: Fast-tempo synthesizer beats (120-130 BPM) with a distinct drop for the transition.
* Audio Prompt: Use local ambient sound for the first 3 seconds, then drop the beat.

3. OPTIMIZED HASHTAG CLUSTER
-----------------------------
#${cat.replace(/ /g, '')} #learnontiktok #techtok #careergoals #dayinmylife #viral2026

4. RETENTION HOOK TIPS
----------------------
* First 2 Seconds: Visual movement (e.g. opening a laptop, pointing at a screen).
* Middle 5 Seconds: Step-by-step breakdown using high-contrast text bubbles.
* Ending: Strong call to action (e.g., "Check link in bio for free tool").`;
}, 'Trends', 'text');

// Batch 2: Marketing & Sales
addTool('email-subject-line-optimizer', 'Email Subject Line Optimizer', 'Maximize your open rates with subject lines that aren\'t spam. We use linguistically optimized patterns to build institutional trust and get your message seen in a crowded inbox.', 'AI & Marketing', [
    { id: 'topic', label: 'Email Main Topic', type: 'text', required: true, minLength: 3 }
], (i: any) => {
    const topic = i.topic || 'New Update';
    return `OPTIMIZED EMAIL SUBJECT LINES FOR: "${topic}"
=====================================================

1. DIRECT & PROFESSIONAL (Best for corporate newsletters)
   * Subject: "Official Update: ${topic}"
   * Open Rate Score: 89% (High Deliverability)
   * Character Count: ${(`Official Update: ${topic}`).length} chars

2. CURIOSITY-DRIVEN (Best for click-through rate)
   * Subject: "The truth about ${topic.toLowerCase()}..."
   * Open Rate Score: 93% (High Engagement)
   * Character Count: ${(`The truth about ${topic.toLowerCase()}...`).length} chars

3. BENEFIT-ORIENTED (Best for promotions)
   * Subject: "Save hours on your next project with ${topic}"
   * Open Rate Score: 91% (High Conversion)
   * Character Count: ${(`Save hours on your next project with ${topic}`).length} chars

4. URGENT / TIME-SENSITIVE (Best for sales/launches)
   * Subject: "Closes soon: Get access to ${topic} now"
   * Open Rate Score: 95% (High Urgency)
   * Character Count: ${(`Closes soon: Get access to ${topic} now`).length} chars

5. CASE STUDY (Best for cold leads)
   * Subject: "How we scaled our ${topic} workflow by 40%"
   * Open Rate Score: 87% (High Authority)
   * Character Count: ${(`How we scaled our ${topic} workflow by 40%`).length} chars`;
}, 'Subject', 'text');

addTool('tagline-generator', 'AI Tagline Generator', 'Build a brand that actually resonates. Our generator analyzes your core values to deliver high-authority taglines that stick. No fluff just high-performance brand architecture.', 'AI & Marketing', [
    { id: 'brand', label: 'Brand / Business Name', type: 'text', placeholder: 'e.g. CorpToolset', required: true, minLength: 2, maxLength: 50 },
    { id: 'focus', label: 'Key Focus / Industry', type: 'text', placeholder: 'e.g. Professional Digital Utilities', required: true, minLength: 3, maxLength: 100 }
], (i: any) => `Tagline: ...`, 'Taglines', 'text', 'tagline');

addTool('brand-name-generator', 'AI Brand Name Generator', 'Naming a brand shouldn\'t be random. We use phonetic analysis and market positioning logic to suggest names that actually project authority and long-term dominance.', 'AI & Marketing', [
    { id: 'desc', label: 'Business Description', type: 'textarea', placeholder: 'e.g. A cloud-native security firm...', required: true, minLength: 10, maxLength: 500 }
], (i: any) => `Names: ...`, 'Brand Names', 'text', 'brand-name');

addTool('ai-newsletter-writer', 'AI Newsletter Writer', 'Curation is hard; engineering it is harder. Generate high-authority newsletters that your subscribers will actually read. Optimized for engagement and professional community building.', 'AI & Marketing', [
    { id: 'topic', label: 'Newsletter Topic', type: 'text', placeholder: 'e.g. Weekly Tech Insights', required: true, minLength: 3, maxLength: 100 }
], (i: any) => `Newsletter: ...`, 'Newsletter', 'text', 'newsletter');

addTool('target-audience-persona-maker', 'Target Audience Persona Maker', 'Stop marketing to everyone. Use precise demographic and psychographic modeling to build personas that actually reflect your ideal customers. High-authority data for serious strategy.', 'AI & Marketing', [
    { id: 'product', label: 'Product / Service Name', type: 'text', placeholder: 'e.g. B2B Project Management App', required: true, minLength: 3 }
], (i: any) => {
    const prod = i.product || 'Your Product';
    return `TARGET CUSTOMER PERSONA FOR: ${prod.toUpperCase()}
===================================================

1. USER PROFILE: "THE BUSY MANAGER"
-----------------------------------
* Name Representation: Sarah Jenkins
* Age Range: 30 - 45 years old
* Job Title: Operations Manager / Team Lead
* Estimated Income: $85,000 - $115,000
* Location: Urban / Suburban areas (Remote-hybrid enabled)

2. CORE PAIN POINTS
-------------------
* Project bottlenecks caused by fragmented communication across multiple platforms.
* Difficulty tracking team deliverables, resulting in missed target dates.
* Cognitive overload from bloated, hard-to-use software interfaces.

3. GOALS & VALUES
-----------------
* Values efficiency, clarity, and software that loads instantly without lag.
* Wants to reduce administrative overhead to focus on core strategic tasks.
* Aims for predictable, stress-free team execution.

4. PURCHASING DECISION DRIVERS
-------------------------------
* Core Objection: "Is it going to take weeks for my team to adopt this tool?"
* Main Incentive: Simple user interface, free tier to validate, and complete privacy compliance.
* Decision Authority: Direct team lead with budget sign-off up to $5,000/year.`;
}, 'Persona', 'text');

addTool('influencer-roi-calculator', 'Influencer Marketing ROI Calculator', 'Calculate influencer ROI.', 'AI & Marketing', [
    { id: 'cost', label: 'Cost', type: 'number', required: true },
    { id: 'rev', label: 'Revenue', type: 'number', required: true }
], (i: any) => `ROI: ${((i.rev - i.cost) / i.cost * 100).toFixed(2)}%`, 'ROI', 'text');

addTool('ai-webinar-script-writer', 'AI Webinar Script Writer', 'Webinars are about retention. Generate scripts that keep your audience engaged and drive actual professional conversion. No generic fluff just structured educational modeling.', 'AI & Marketing', [
    { id: 'topic', label: 'Topic / Main Title', type: 'text', placeholder: 'e.g. Scaling Your SaaS Startup', required: true, minLength: 3, maxLength: 100 },
    { id: 'product', label: 'Product / Service to Pitch', type: 'text', placeholder: 'e.g. Apex Growth Platform', required: true, minLength: 3 }
], (i: any) => {
    const topic = i.topic || 'General Topic';
    const prod = i.product || 'Your Service';
    return `WEBINAR PRESENTATION OUTLINE & SCRIPT FOR: "${topic}"
========================================================

PART 1: THE HOOK & INTRODUCTION (0 - 10 minutes)
-----------------------------------------------
* Visual: Title slide displaying topic name.
* Speaker Script: "Welcome everyone! Today, we are breaking down the exact blueprint for ${topic}. If you've been struggling to scale or find a repeatable framework, you are in the right place. In the next 45 minutes, we will cover three practical shifts you can implement today."

PART 2: THE CORE CONTEXT / PROBLEM (10 - 25 minutes)
---------------------------------------------------
* Visual: Case study charts / workflow bottlenecks.
* Speaker Script: "Before we look at the solutions, let's address why traditional models fail. Most teams focus on vanity metrics. We need to shift our focus to real pipeline velocity and client-side execution."

PART 3: THE THREE SECRET TAKEAways (25 - 45 minutes)
---------------------------------------------------
* Secret 1: High-Speed local performance beats cloud server queues.
* Secret 2: Clean documentation and clear structures reduce team friction.
* Secret 3: Transparent privacy alignment retains enterprise clients.

PART 4: THE PITCH / PRODUCT INTRO (45 - 55 minutes)
--------------------------------------------------
* Visual: Features of ${prod}.
* Speaker Script: "Now, you can implement all of this manually, or you can leverage ${prod} to automate 90% of the workflow. We built this to help professionals like you optimize execution with complete privacy."

PART 5: AUDIENCE Q&A & CALL-TO-ACTION (55 - 60 minutes)
------------------------------------------------------
* Speaker Script: "We are opening the floor for questions. Remember, the special offer for ${prod} closes tonight. Head to our site to lock in your early access."`;
}, 'Script', 'text');

addTool('affiliate-link-shortener', 'Affiliate Link Shortener', 'Create clean, easy-to-read links to replace long, messy affiliate URLs. This tool helps you organize referral and redirect paths locally to make your links look clean and professional on social media or email.', 'AI & Marketing', [
    { id: 'url', label: 'Target Affiliate URL', type: 'text', placeholder: 'https://affiliate.com/id=123', required: true, minLength: 10 },
    { id: 'domain', label: 'Your Domain', type: 'text', placeholder: 'https://corptoolset.com', required: true, minLength: 8 },
    { id: 'slug', label: 'Desired Slug', type: 'text', placeholder: 'e.g. premium-deal', required: true, minLength: 3, maxLength: 50 }
], (i: any) => {
    const domain = i.domain.replace(/\/$/, '');
    const cleanSlug = i.slug.replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase();
    const shortened = `${domain}/recommends/${cleanSlug}`;
    
    return `SHORTENED REFERRAL URL
======================
Link: ${shortened}

SERVER REDIRECT SETUP GUIDE
---------------------------
To make this shortened link active on your website, implement one of these redirect configurations:

1. NEXT.JS REDIRECTS (next.config.js / next.config.ts)
Add this object inside your redirects() array:
{
  source: '/recommends/${cleanSlug}',
  destination: '${i.url}',
  permanent: true
}

2. NGINX REWRITE RULE (nginx.conf)
Add this line inside your server block:
rewrite ^/recommends/${cleanSlug}$ ${i.url} permanent;

3. APACHE REDIRECT (.htaccess)
Add this line at the bottom of your file:
Redirect 301 /recommends/${cleanSlug} ${i.url}`;
}, 'Shortened Link', 'text');

addTool('gmb-post-generator', 'Local Business Profile Post Generator', 'Generate updates and promotional posts for your local business profile listing. Input your company details to draft updates that attract customers in local search results.', 'AI & Marketing', [
    { id: 'biz', label: 'Business Name', type: 'text', placeholder: 'e.g. CorpToolset Labs', required: true, minLength: 2, maxLength: 50 },
    { id: 'type', label: 'Post Type', type: 'select', defaultValue: 'offer', options: [
        { label: 'Offer / Special Discount', value: 'offer' },
        { label: 'What\'s New / Product Update', value: 'update' },
        { label: 'Event Promotion', value: 'event' }
    ]},
    { id: 'details', label: 'Context / Key Details', type: 'textarea', placeholder: 'e.g. 20% off our audit services this week only.', required: true }
], (i: any) => {
    const biz = i.biz || 'Our Business';
    const type = i.type || 'offer';
    const details = i.details || '';
    
    let header = '';
    let cta = '';
    if (type === 'offer') {
        header = `🚨 SPECIAL OFFER FROM ${biz.toUpperCase()} 🚨`;
        cta = 'Call to Action: Click "Get Offer" below to claim this discount today!';
    } else if (type === 'update') {
        header = `📢 WHAT'S NEW AT ${biz.toUpperCase()}?`;
        cta = 'Call to Action: Click "Learn More" to read the full specifications on our site.';
    } else {
        header = `📅 UPCOMING EVENT: HOSTED BY ${biz.toUpperCase()}`;
        cta = 'Call to Action: Click "Sign Up" to secure your virtual seat.';
    }

    return `${header}
--------------------
${details}

Why choose ${biz}?
* 100% Client-Side Processing.
* Instant verification and maximum security.
* No accounts or signup forms required.

${cta}

#LocalSearch #${biz.replace(/[^a-zA-Z0-9]/g, '')} #CommunityUpdate #BusinessPromotion`;
}, 'Post', 'text');

addTool('sales-script-generator', 'Sales Script Generator', 'Create structured sales scripts and answers to common customer objections. This generator helps you structure pitches for different products to improve sales conversations.', 'AI & Marketing', [
    { id: 'prod', label: 'Product Name', type: 'text', placeholder: 'e.g. Enterprise Cloud Suite', required: true, minLength: 3, maxLength: 100 },
    { id: 'title', label: 'Prospect Job Title', type: 'text', placeholder: 'e.g. CTO, Head of IT, Founder', required: true },
    { id: 'objection', label: 'Main Objection', type: 'text', placeholder: 'e.g. Budget constraints, No time', required: true }
], (i: any) => {
    const prod = i.prod || 'Our Service';
    const title = i.title || 'Decision Maker';
    const objection = i.objection || 'Price is too high';
    return `PROFESSIONAL SALES OUTLINE FOR PITCHING: ${prod.toUpperCase()}
=========================================================
Target Prospect: ${title}
Target Objection: "${objection}"

1. THE COLD OPEN / HOOK (First 15 seconds)
------------------------------------------
"Hi, [Prospect Name], this is [My Name] from ${prod}. I noticed you're heading the operations team at your company. We've been helping other ${title}s reduce their system processing times by 40% while ensuring 100% offline data privacy. Do you have 2 minutes to see if we can do the same for you?"

2. QUALIFICATION & DISCOVERY QUESTIONS
--------------------------------------
* "How are you currently managing data safety and compliance when using online formatting tools?"
* "What is the biggest bottleneck your development team faces during peak deployment schedules?"
* "If you could run all file operations 100% locally with zero server queues, how much time would that save you?"

3. TAILORED VALUE PROPOSITION
-----------------------------
"For a ${title}, the main concern is compliance and speed. With ${prod}, everything runs locally in the browser. You get standard compliance alignment immediately, and your team is never slowed down by network latency."

4. OBJECTION HANDLING SCRIPT
-----------------------------
* Prospect: "${objection}."
* Your Response: "I completely understand that. Many ${title}s we work with initially had the same concern. However, what they found was that by executing queries locally, they eliminated security audit bottlenecks entirely, paying for the tool in the first week. We can start with a free local validation test if that works better?"

5. CALL TO ACTION / CLOSE
-------------------------
"Do you have your calendar handy? Let's schedule a brief 10-minute walkthrough next Tuesday morning to show you the local benchmarks."`;
}, 'Script', 'text');

// Batch 3: Converters
addTool('yaml-to-json-converter', 'YAML to JSON Converter', 'Convert YAML configurations to clean JSON files locally in your browser.', 'Developer', [
    { id: 'yaml', label: 'YAML Input', type: 'textarea', placeholder: 'server:\n  port: 8080\n  host: localhost', required: true }
], (i: any) => {
    const yaml = i.yaml || '';
    if (!yaml.trim()) return 'Please enter YAML.';
    try {
        const lines = yaml.split('\n');
        const result: any = {};
        for (let line of lines) {
            line = line.trim();
            if (!line || line.startsWith('#')) continue;
            const match = line.match(/^([^:]+):(.*)$/);
            if (match) {
                const key = match[1].trim();
                let val = match[2].trim();
                if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
                    val = val.substring(1, val.length - 1);
                }
                if (val === 'true') {
                    result[key] = true;
                } else if (val === 'false') {
                    result[key] = false;
                } else if (!isNaN(Number(val)) && val !== '') {
                    result[key] = Number(val);
                } else {
                    result[key] = val;
                }
            }
        }
        return JSON.stringify(result, null, 2);
    } catch (e: any) {
        return 'YAML Parse Error: ' + e.message;
    }
}, 'JSON Output', 'json', 'generic');

addTool('json-to-csv-converter', 'JSON to CSV Converter', 'Convert structured JSON arrays to clean, spreadsheet-ready CSV strings locally.', 'Developer', [
    { id: 'json', label: 'JSON Input', type: 'textarea', placeholder: '[\n  {"id": 1, "name": "John"},\n  {"id": 2, "name": "Jane"}\n]', required: true }
], (i: any) => {
    const jsonStr = i.json || '';
    if (!jsonStr.trim()) return 'Please enter JSON.';
    try {
        const data = JSON.parse(jsonStr);
        const arr = Array.isArray(data) ? data : [data];
        if (arr.length === 0) return 'Empty dataset.';
        const headers = Array.from(new Set(arr.flatMap(item => Object.keys(item))));
        const csvRows = [];
        csvRows.push(headers.join(','));
        for (const row of arr) {
            const values = headers.map(header => {
                const val = row[header];
                const valStr = val === null || val === undefined ? '' : String(val);
                const escaped = valStr.replace(/"/g, '""');
                return escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')
                    ? `"${escaped}"`
                    : escaped;
            });
            csvRows.push(values.join(','));
        }
        return csvRows.join('\n');
    } catch (e: any) {
        return 'Invalid JSON: ' + e.message;
    }
}, 'CSV Output', 'text', 'generic');

addTool('xml-to-json-converter', 'XML to JSON Converter', 'Convert legacy XML documents to modern JSON schemas instantly in your browser.', 'Developer', [
    { id: 'xml', label: 'XML Input', type: 'textarea', placeholder: '<root>\n  <user id="1">John</user>\n</root>', required: true }
], (i: any) => {
    const xml = i.xml || '';
    if (!xml.trim()) return 'Please enter XML content.';
    if (typeof window === 'undefined') return 'Processing...';
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, 'text/xml');
        const parserError = xmlDoc.getElementsByTagName('parsererror');
        if (parserError.length > 0) {
            return 'Invalid XML: ' + parserError[0].textContent;
        }
        
        const xmlToJson = (node: Node): any => {
            if (node.nodeType === 3) return node.nodeValue;
            const obj: any = {};
            if (node.nodeType === 1) {
                const element = node as Element;
                if (element.attributes.length > 0) {
                    obj['@attributes'] = {};
                    for (let j = 0; j < element.attributes.length; j++) {
                        const attribute = element.attributes.item(j);
                        if (attribute) {
                            obj['@attributes'][attribute.nodeName] = attribute.nodeValue;
                        }
                    }
                }
            }
            if (node.hasChildNodes()) {
                for (let j = 0; j < node.childNodes.length; j++) {
                    const item = node.childNodes.item(j);
                    const nodeName = item.nodeName;
                    if (nodeName === '#text') {
                        const val = item.nodeValue?.trim();
                        if (val) return val;
                        continue;
                    }
                    if (obj[nodeName] === undefined) {
                        obj[nodeName] = xmlToJson(item);
                    } else {
                        if (!Array.isArray(obj[nodeName])) {
                            obj[nodeName] = [obj[nodeName]];
                        }
                        obj[nodeName].push(xmlToJson(item));
                    }
                }
            }
            return obj;
        };
        const result = xmlToJson(xmlDoc.documentElement);
        return JSON.stringify({ [xmlDoc.documentElement.nodeName]: result }, null, 2);
    } catch (e: any) {
        return 'Error parsing XML: ' + e.message;
    }
}, 'JSON Output', 'json', 'generic');

addTool('html-to-jsx-converter', 'HTML to JSX Converter', 'Convert HTML to JSX.', 'Developer', [
    { id: 'html', label: 'HTML Input', type: 'textarea' }
], (i: any) => {
    let html = i.html || '';
    // Replace class= with className=
    html = html.replace(/class\s*=\s*(['"])(.*?)\1/g, 'className=$1$2$1');
    // Replace for= with htmlFor=
    html = html.replace(/for\s*=\s*(['"])(.*?)\1/g, 'htmlFor=$1$2$1');
    // Close self-closing tags (img, input, br, hr) if not closed
    html = html.replace(/<(img|input|br|hr|meta|link)([^>]*?)(?<!\/)>/gi, '<$1$2 />');
    // Convert inline style strings to JSX style objects
    html = html.replace(/style\s*=\s*(['"])(.*?)\1/g, (match: string, quote: string, styleStr: string) => {
        const rules = styleStr.split(';').filter(r => r.trim());
        const reactRules = rules.map(rule => {
            const parts = rule.split(':');
            if (parts.length < 2) return '';
            const key = parts[0].trim().replace(/-([a-z])/g, (m, c) => c.toUpperCase());
            const val = parts.slice(1).join(':').trim();
            return `${key}: "${val.replace(/"/g, '\\"')}"`;
        }).filter(Boolean);
        return `style={{ ${reactRules.join(', ')} }}`;
    });
    return html;
}, 'JSX Output', 'code', 'generic');

addTool('qr-generator', 'Free QR Code Generator Online', 'Create secure QR codes instantly.', 'Web & SEO', [
    { id: 'data', label: 'QR Content', type: 'textarea', placeholder: 'Enter URL or Text', required: true, minLength: 1, maxLength: 2000 }
], (i: any) => i.data, 'QR Code', 'image', 'qr-generator');

addTool('json-formatter', 'Free JSON Formatter Online', 'Free JSON beautifier and validator (Private & Fast).', 'Developer', [
    { id: 'json', label: 'JSON Input', type: 'textarea' }
], (i: any) => i.json, 'Formatted JSON', 'json', 'json');

addTool('base64-encode', 'Base64 Encode Online', 'Convert plain text and files to Base64 encoded strings instantly in your browser (100% private).', 'Developer', [
    { id: 'text', label: 'Input Text', type: 'textarea' }
], (i: any) => btoa(unescape(encodeURIComponent(i.text))), 'Encoded Base64', 'text', 'text-utilities', { defaultTool: 'base64-encode' });

addTool('base64-decode', 'Base64 Decode Online', 'Decode Base64 strings back into readable plain text instantly in your browser (100% private).', 'Developer', [
    { id: 'text', label: 'Base64 Input', type: 'textarea' }
], (i: any) => {
    try {
        return decodeURIComponent(escape(atob(i.text)));
    } catch {
        return 'Invalid Base64';
    }
}, 'Decoded Text', 'text', 'text-utilities', { defaultTool: 'base64-decode' });

addTool('password-generator', 'Secure Password Generator', 'Generate secure random passwords instantly for maximum security.', 'Security', [
    { id: 'len', label: 'Length', type: 'number', defaultValue: '16', min: 4, max: 128, required: true }
], (i: any) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+~`|}{[]:;?><,./-+=';
    const len = parseInt(i.len) || 16;
    let pass = '';
    for (let j = 0; j < len; j++) {
        pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
}, 'Generated Password', 'text', 'password');

addTool('username-generator', 'Free Username Generator Online', 'Create brand-safe usernames instantly.', 'AI & Marketing', [
    { id: 'name', label: 'Seed Name', type: 'text' }
], (i: any) => `${i.name}_pro`, 'Usernames', 'text', 'username');

addTool('grammar-checker', 'Free Grammar Checker Online', 'Free online proofreading (Private & Fast).', 'Content', [
    { id: 'text', label: 'Text', type: 'textarea' }
], (i: any) => i.text, 'Checked Text', 'text', 'grammar');

addTool('jwt-debugger', 'Free JWT Debugger Online', 'Decode and audit JSON Web Tokens instantly (100% Private).', 'Security', [
    { id: 'jwt', label: 'JWT Token', type: 'textarea' }
], (i: any) => i.jwt, 'Decoded Token', 'json', 'jwt-debugger');

addTool('bcrypt-hasher', 'Bcrypt Hasher & Checker', 'Securely hash and verify passwords.', 'Security', [
    { id: 'pass', label: 'Password', type: 'text' }
], (i: any) => i.pass, 'Hash', 'text', 'bcrypt');

addTool('sql-query-auditor', 'SQL Query Safety Auditor', 'Audit queries for syntax safety and structure validation checks.', 'Security', [
    { id: 'query', label: 'SQL Query', type: 'textarea', placeholder: 'SELECT * FROM users WHERE id = 1;', required: true }
], (i: any) => {
    const query = i.query || '';
    const patterns = [
        { pattern: "OR '1'='1", reason: "Potential tautology bypass pattern.", severity: 'HIGH' },
        { pattern: "UNION SELECT", reason: "Potential unauthorized cross-table data exposure risk.", severity: 'HIGH' },
        { pattern: "DROP TABLE", reason: "Potential destructive table drop operation.", severity: 'HIGH' },
        { pattern: "--", reason: "Standard SQL comment sequence used to truncate parsing logic.", severity: 'MEDIUM' },
        { pattern: "/*", reason: "Multi-line comment indicator which may mask query segments.", severity: 'MEDIUM' }
    ];
    const foundRisks = patterns.filter(p => query.toLowerCase().includes(p.pattern.toLowerCase()));
    if (foundRisks.length === 0) {
        return `SQL QUERY VALIDATION REPORT\n============================\nStatus: SAFE (No threat patterns detected)\nQuery Length: ${query.length} characters\n\nRecommendations:\n1. Ensure you use parameterized queries or prepared statements in your database driver.\n2. Avoid concatenating input strings directly into SQL queries.`;
    }
    const riskDetails = foundRisks.map(r => `[${r.severity}] pattern: "${r.pattern}" -> Reason: ${r.reason}`).join('\n');
    return `SQL QUERY VALIDATION REPORT\n============================\nStatus: WARNING (${foundRisks.length} issue(s) detected)\nQuery Length: ${query.length} characters\n\nDetected Patterns:\n${riskDetails}\n\nRecommendations:\n1. Escape inputs and use parameterized queries.\n2. Sanitize user-provided values before DB compilation.`;
}, 'Audit Report', 'text', 'sql-checker');

// Batch 4: Design & Web
addTool('css-box-shadow-generator', 'Free CSS Box Shadow Generator', 'Free CSS UI design utility (Fast & Easy).', 'Developer', [
    { id: 'h', label: 'Horizontal Offset (px)', type: 'number', defaultValue: '10' },
    { id: 'v', label: 'Vertical Offset (px)', type: 'number', defaultValue: '10' },
    { id: 'blur', label: 'Blur Radius (px)', type: 'number', defaultValue: '20' },
    { id: 'spread', label: 'Spread Radius (px)', type: 'number', defaultValue: '0' },
    { id: 'color', label: 'Shadow Color (Hex)', type: 'text', defaultValue: '#000000' },
    { id: 'opacity', label: 'Opacity (0 - 1)', type: 'number', defaultValue: '0.15' }
], (i: any) => {
    const h = i.h ?? 10;
    const v = i.v ?? 10;
    const blur = i.blur ?? 20;
    const spread = i.spread ?? 0;
    const color = i.color || '#000000';
    const opacity = i.opacity ?? 0.15;
    let hex = color.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map((c: string) => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    const rgba = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    return `box-shadow: ${h}px ${v}px ${blur}px ${spread}px ${rgba};\n-webkit-box-shadow: ${h}px ${v}px ${blur}px ${spread}px ${rgba};\n-moz-box-shadow: ${h}px ${v}px ${blur}px ${spread}px ${rgba};`;
}, 'CSS Output', 'code', 'generic');

addTool('css-animation-generator', 'Free CSS Animation Generator', 'Create smooth, custom CSS keyframe animations online.', 'Developer', [
    { id: 'name', label: 'Animation Name', type: 'text', defaultValue: 'fadeIn' },
    { id: 'type', label: 'Animation Type', type: 'select', defaultValue: 'fade', options: [
        { label: 'Fade In', value: 'fade' },
        { label: 'Slide In Left', value: 'slideLeft' },
        { label: 'Pulse Scale', value: 'pulse' },
        { label: 'Rotate Spin', value: 'spin' },
        { label: 'Bounce', value: 'bounce' }
    ]},
    { id: 'duration', label: 'Duration (seconds)', type: 'number', defaultValue: '1' }
], (i: any) => {
    const name = (i.name || 'animation').trim().replace(/[^a-zA-Z0-9_-]/g, '');
    const type = i.type || 'fade';
    const duration = i.duration || '1';
    let keyframes = '';
    switch (type) {
        case 'fade':
            keyframes = `@keyframes ${name} {\n  from { opacity: 0; }\n  to { opacity: 1; }\n}`;
            break;
        case 'slideLeft':
            keyframes = `@keyframes ${name} {\n  from { transform: translateX(-100%); opacity: 0; }\n  to { transform: translateX(0); opacity: 1; }\n}`;
            break;
        case 'pulse':
            keyframes = `@keyframes ${name} {\n  0%, 100% { transform: scale(1); }\n  50% { transform: scale(1.05); }\n}`;
            break;
        case 'spin':
            keyframes = `@keyframes ${name} {\n  from { transform: rotate(0deg); }\n  to { transform: rotate(360deg); }\n}`;
            break;
        case 'bounce':
            keyframes = `@keyframes ${name} {\n  0%, 100% { transform: translateY(0); animation-timing-function: cubic-bezier(0.8,0,1,1); }\n  50% { transform: translateY(-25%); animation-timing-function: cubic-bezier(0,0,0.2,1); }\n}`;
            break;
    }
    return `${keyframes}\n\n.animate-${name} {\n  animation: ${name} ${duration}s ease-in-out infinite;\n}`;
}, 'CSS Output', 'code', 'generic');

addTool('js-keycode-finder', 'Free JavaScript KeyCode Finder', 'Find keyboard Event.keyCode values instantly.', 'Developer', [
    { id: 'key', label: 'Key character or name', type: 'text', placeholder: 'e.g. Enter, a, Space', required: true }
], (i: any) => {
    const key = i.key || '';
    if (!key) return 'Please type a key.';
    if (key.length === 1) {
        return `Key Name: "${key}"\nKeyCode: ${key.charCodeAt(0)}\nCode: Key${key.toUpperCase()}\nLocation: Standard`;
    }
    const specialKeys: Record<string, number> = {
        'enter': 13, 'escape': 27, 'space': 32, 'backspace': 8, 'tab': 9,
        'shift': 16, 'control': 17, 'alt': 18, 'pause': 19, 'capslock': 20,
        'pageup': 33, 'pagedown': 34, 'end': 35, 'home': 36, 'arrowleft': 37,
        'arrowup': 38, 'arrowright': 39, 'arrowdown': 40, 'insert': 45, 'delete': 46
    };
    const normKey = key.toLowerCase().replace(/ /g, '');
    if (specialKeys[normKey] !== undefined) {
        return `Key Name: "${key}"\nKeyCode: ${specialKeys[normKey]}\nCode: ${key}\nLocation: Standard`;
    }
    return `Key Name: "${key}"\nKeyCode: unknown\n(Try single characters or common key names like Enter, Escape, Space)`;
}, 'Result', 'text', 'generic');

addTool('gst-calculator-india', 'GST Calculator India', 'Calculate Goods and Services Tax (GST) in India with our free online GST calculator. Easily compute CGST, SGST, and IGST components for inclusive or exclusive rates.', 'Financial', [
    { id: 'amt', label: 'Amount', type: 'number' }
], (i: any) => i.amt, 'Result', 'text', 'financial-calculator', { defaultTool: 'gst' }, ['calculate', 'compute', 'audit'], [
    'gst calculator',
    'tax calculator',
    'gst tax calculator',
    'online gst calculator',
    'gst calculator online',
    'free gst calculator',
    'gst calculator india'
]);


addTool('emi-calculator', 'EMI Calculator Pro', 'Calculate monthly loan EMI payments online with our free tool. Plan home loans, car loans, and personal loans with a detailed breakdown.', 'Financial', [
    { id: 'p', label: 'Principal', type: 'number' }
], (i: any) => i.p, 'Result', 'text', 'financial-calculator', { defaultTool: 'emi' }, ['calculate', 'compute', 'plan'], [
    'emi calculator',
    'loan emi calculator',
    'loan calculator',
    'emi calculator online',
    'monthly emi calculator'
]);

// Override metadata specifically for SEO targeting on requested keywords
toolRegistry['emi-calculator'].metadata = {
    ...toolRegistry['emi-calculator'].metadata,
    title: 'EMI Calculator – Free Online Monthly Loan EMI Calculator',
    description: 'Calculate your loan EMI (Equated Monthly Installment) online with our free EMI calculator. Plan home loans, car loans, and personal loans with a detailed breakdown.',
    keywords: [
        'emi calculator',
        'loan emi calculator',
        'loan calculator',
        'emi calculator online',
        'monthly emi calculator',
        'calculate loan emi',
        'monthly loan calculator',
        'free emi calculator'
    ]
};

addTool('income-tax-calculator', 'Income Tax Calculator FY 2024-25', 'Calculate your annual income tax liability in India under new and old tax regimes with our free online calculator.', 'Financial', [
    { id: 'inc', label: 'Annual Income', type: 'number' }
], (i: any) => i.inc, 'Tax', 'text', 'financial-calculator', { defaultTool: 'incometax' }, ['calculate', 'estimate', 'audit'], [
    'income tax calculator',
    'tax calculator',
    'tax calculator india'
]);

// Override metadata specifically for SEO targeting on requested keywords
toolRegistry['income-tax-calculator'].metadata = {
    ...toolRegistry['income-tax-calculator'].metadata,
    title: 'Income Tax Calculator FY 2024-25 – Free Tax Calculator India',
    description: 'Calculate your annual income tax liability in India under new and old tax regimes with our free online income tax calculator. Compute taxable income, deductions, and tax splits.',
    keywords: [
        'income tax calculator',
        'tax calculator',
        'tax calculator india',
        'income tax calculator india',
        'calculate income tax',
        'free tax calculator online'
    ]
};

addTool('loan-calculator', 'Free Loan Calculator Online', 'Plan your loans with a detailed breakdown (Private & Fast).', 'Financial', [
    { id: 'amt', label: 'Loan Amount', type: 'number' }
], (i: any) => i.amt, 'Result', 'text', 'financial-calculator', { defaultTool: 'loan' });

addTool('percentage-calculator', 'Percentage Calculator', 'Solve percent problems.', 'Financial', [
    { id: 'val', label: 'Value', type: 'number' }
], (i: any) => i.val, 'Result', 'text', 'financial-calculator', { defaultTool: 'percentage' });

addTool('age-calculator', 'Age Calculator', 'Precise age determination.', 'Financial', [
    { id: 'dob', label: 'Birth Date', type: 'date' }
], (i: any) => i.dob, 'Age', 'text', 'financial-calculator', { defaultTool: 'age' });

addTool('salary-calculator-india', 'Salary Calculator India', 'Calculate take-home pay.', 'Financial', [
    { id: 'sal', label: 'Annual CTC', type: 'number' }
], (i: any) => i.sal, 'Salary', 'text', 'financial-calculator', { defaultTool: 'salary' });

addTool('profit-margin-calculator', 'Profit Margin Calculator', 'Analyze business margins.', 'Financial', [
    { id: 'cost', label: 'Cost', type: 'number' },
    { id: 'rev', label: 'Revenue', type: 'number' }
], (i: any) => i.rev, 'Margin', 'text', 'financial-calculator', { defaultTool: 'profit' });

addTool('discount-calculator', 'Discount Calculator', 'Calculate final prices.', 'Financial', [
    { id: 'price', label: 'Original Price', type: 'number' },
    { id: 'disc', label: 'Discount %', type: 'number' }
], (i: any) => i.price, 'Final Price', 'text', 'financial-calculator', { defaultTool: 'discount' });

addTool('time-duration-calculator', 'Time Duration Calculator', 'Calculate time difference.', 'Financial', [
    { id: 's', label: 'Start', type: 'time' }
], (i: any) => i.s, 'Duration', 'text', 'financial-calculator', { defaultTool: 'duration' });

addTool('scientific-calculator', 'Free Scientific Calculator Online', 'Advanced mathematical calculator (100% Private).', 'Developer', [
    { id: 'exp', label: 'Expression', type: 'text', placeholder: 'e.g. Math.sin(45) * Math.log(100)', required: true, minLength: 1, maxLength: 500 }
], (i: any) => {
    try {
        // Safe evaluation of mathematical expressions using Function constructor
        // We prefix with 'Math.' if not present for common functions
        let expression = i.exp.replace(/sin|cos|tan|log|pow|sqrt|PI|E/g, (m: string) => `Math.${m}`);
        return new Function(`return ${expression}`)();
    } catch (e) {
        return 'Invalid Expression';
    }
}, 'Result', 'text');

addTool('bmi-calculator', 'BMI Calculator', 'Calculate your Body Mass Index (BMI) instantly using your height and weight. This tool provides standard weight classifications (Underweight, Normal, Overweight, Obese) to help monitor your wellness goals.', 'Financial', [], (i: any) => '', 'BMI', 'text', 'financial-calculator', { defaultTool: 'bmi' });

addTool('currency-converter', 'Currency Converter', 'Convert values between different global currencies locally and securely.', 'Financial', [
    { id: 'amt', label: 'Amount', type: 'number', defaultValue: '100', required: true },
    { id: 'from', label: 'From Currency', type: 'select', defaultValue: 'USD', options: [
        { label: 'USD - US Dollar', value: 'USD' },
        { label: 'EUR - Euro', value: 'EUR' },
        { label: 'GBP - British Pound', value: 'GBP' },
        { label: 'INR - Indian Rupee', value: 'INR' },
        { label: 'JPY - Japanese Yen', value: 'JPY' },
        { label: 'CAD - Canadian Dollar', value: 'CAD' },
        { label: 'AUD - Australian Dollar', value: 'AUD' }
    ]},
    { id: 'to', label: 'To Currency', type: 'select', defaultValue: 'INR', options: [
        { label: 'INR - Indian Rupee', value: 'INR' },
        { label: 'USD - US Dollar', value: 'USD' },
        { label: 'EUR - Euro', value: 'EUR' },
        { label: 'GBP - British Pound', value: 'GBP' },
        { label: 'JPY - Japanese Yen', value: 'JPY' },
        { label: 'CAD - Canadian Dollar', value: 'CAD' },
        { label: 'AUD - Australian Dollar', value: 'AUD' }
    ]}
], (i: any) => {
    const rates: Record<string, number> = {
        USD: 1.0, EUR: 0.92, GBP: 0.78, INR: 83.5, JPY: 158.2, CAD: 1.36, AUD: 1.50
    };
    const amt = parseFloat(i.amt) || 0;
    const from = i.from || 'USD';
    const to = i.to || 'INR';
    const usdAmount = amt / (rates[from] || 1);
    const converted = usdAmount * (rates[to] || 1);
    return `${amt} ${from} = ${converted.toFixed(2)} ${to}\n\n(Calculated locally using offline exchange rates last updated July 2026)`;
}, 'Result', 'text', 'generic');

// Batch 6: SEO
addTool('robots-txt-generator', 'Free Robots.txt Generator', 'Create SEO-friendly robots.txt files instantly.', 'Web & SEO', [
    { id: 'url', label: 'Website Domain', type: 'text', placeholder: 'https://corptoolset.com', required: true, minLength: 8 },
    { id: 'p', label: 'Disallow Path (e.g. /admin)', type: 'text', placeholder: 'e.g. /admin/', required: true, minLength: 1 }
], (i: any) => `User-agent: *\nDisallow: ${i.p}\n\nSitemap: ${i.url.replace(/\/$/, '')}/sitemap.xml`, 'Content', 'code');

addTool('xml-sitemap-generator', 'XML Sitemap Generator', 'Create an XML sitemap for your website. Generate lists of your website pages to help search engines discover and index your site content efficiently.', 'Web & SEO', [
    { id: 'url', label: 'Base URL', type: 'text', placeholder: 'https://example.com', required: true, minLength: 8 }
], (i: any) => `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${i.url}/</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
    <priority>1.0</priority>
  </url>
</urlset>`, 'Sitemap XML', 'code');

addTool('meta-tag-generator', 'Free Meta Tag Generator Online', 'Generate SEO meta tags for your website.', 'Web & SEO', [
    { id: 't', label: 'Page Title', type: 'text', placeholder: 'e.g. CorpToolset - Professional Utility Suite', required: true, minLength: 5, maxLength: 70 },
    { id: 'd', label: 'Meta Description', type: 'textarea', placeholder: 'Enter description...', required: true, minLength: 50, maxLength: 160 }
], (i: any) => `<title>${i.t}</title>
<meta name="title" content="${i.t}">
<meta name="description" content="${i.d}">
<meta property="og:title" content="${i.t}">
<meta property="og:description" content="${i.d}">`, 'Meta Tags', 'code');

// Category Aggregators
addTool('text-utilities', 'Text & Content Utilities', 'Professional text processing tools.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities');
addTool('pdf-tools', 'PDF Management Suite', 'Industrial PDF manipulation tools.', 'Developer', [], (i: any) => '', 'Result', 'text', 'pdf-merge');
addTool('financial-tools', 'Financial Analysis Suite', 'Professional calculators and fiscal tools.', 'Financial', [], (i: any) => '', 'Result', 'text', 'financial-calculator');
addTool('security-tools', 'Security & Privacy Suite', 'Secure encryption, hashing, and privacy-related tools.', 'Security', [], (i: any) => '', 'Result', 'text', 'password');
addTool('ai-marketing-tools', 'AI & Marketing Suite', 'High-authority AI prompt and marketing generators.', 'AI & Marketing', [], (i: any) => '', 'Result', 'text', 'ai-prompt-generator');
addTool('developer-tools', 'Developer & Data Suite', 'Industrial data transformation and dev utilities.', 'Developer', [], (i: any) => '', 'Result', 'text', 'json');
addTool('web-seo-tools', 'Web & SEO Suite', 'Professional search engine optimization tools.', 'Web & SEO', [], (i: any) => '', 'Result', 'text', 'text-utilities');

addTool('merge-pdf', 'Merge PDF Online Free', 'Combine multiple PDF files into one document instantly. Our free online PDF merger works entirely in your browser – no file uploads, no sign-up required. Merge PDF files of any size securely and privately.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'pdf-merge', {}, ['merge', 'combine', 'join'], ['combine pdf files', 'merge pdf online', 'join pdf files free', 'pdf merger']);
addTool('split-pdf', 'Split PDF Online Free', 'Split a PDF into individual pages or extract specific page ranges instantly. Our free PDF splitter works 100% in your browser with no file uploads. Separate PDF pages in seconds.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'pdf-split', {}, ['split', 'separate', 'extract'], ['split pdf pages', 'pdf page extractor', 'separate pdf pages', 'pdf splitter free']);
addTool('compress-pdf', 'Compress PDF Online Free', 'Reduce PDF file size instantly without losing quality. Our free PDF compressor works entirely in your browser – no uploads, no account needed. Shrink PDF files for email and web sharing.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'pdf-compress', {}, ['compress', 'reduce', 'shrink'], ['pdf compressor', 'reduce pdf size', 'compress pdf file', 'pdf size reducer']);
addTool('jpg-to-pdf', 'JPG to PDF Converter Online Free', 'Convert JPG, PNG and image files to PDF instantly. Free online image to PDF converter – works in your browser, no uploads required. Create PDF from photos and images in seconds.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'jpg-to-pdf', {}, ['convert', 'create', 'make'], ['image to pdf', 'png to pdf', 'convert jpg to pdf', 'photo to pdf']);
/*
addTool('pdf-to-jpg', 'PDF to JPG Converter Online Free', 'Convert PDF pages to high-quality JPG images instantly. Free online PDF to image converter that works in your browser with no file uploads. Extract images from PDF files securely.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'pdf-merge', {}, ['convert', 'extract', 'export'], ['pdf to image', 'pdf to jpeg', 'convert pdf to jpg', 'pdf image extractor']);
addTool('word-to-pdf', 'Word to PDF Converter Online Free', 'Convert Word documents (DOC, DOCX) to PDF instantly. Free online Word to PDF converter that runs in your browser with no file uploads. Create PDF from Word files securely.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'pdf-merge', {}, ['convert', 'export', 'save'], ['doc to pdf', 'docx to pdf', 'microsoft word to pdf', 'word document to pdf']);
addTool('excel-to-pdf', 'Excel to PDF Converter Online Free', 'Convert Excel spreadsheets (XLS, XLSX) to PDF instantly. Free online Excel to PDF converter that works in your browser with no uploads required. Turn spreadsheets into PDF files.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'pdf-merge', {}, ['convert', 'export', 'transform'], ['xls to pdf', 'xlsx to pdf', 'spreadsheet to pdf', 'excel file to pdf']);
addTool('powerpoint-to-pdf', 'PowerPoint to PDF Converter Online Free', 'Convert PowerPoint presentations (PPT, PPTX) to PDF instantly. Free online PPT to PDF converter – no uploads, no account needed. Turn slides into PDF documents in seconds.', 'PDF Tools', [], (i: any) => '', 'Result', 'text', 'pdf-merge', {}, ['convert', 'export', 'save'], ['ppt to pdf', 'pptx to pdf', 'presentation to pdf', 'powerpoint converter']);
*/
addTool('gpa-to-percentage-calculator', 'GPA to Percentage Converter', 'Academic score conversion with industrial precision.', 'Financial', [], (i: any) => '', 'Percentage', 'text', 'financial-calculator', { defaultTool: 'gpa' });
addTool('random-number-generator', 'Random Number Generator', 'Generate verifiable random digits within a range.', 'Security', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'random-number' });
addTool('random-word-generator', 'Random Word Generator', 'Generate random vocabulary for inspiration or testing.', 'Content', [], (i: any) => '', 'Word', 'text', 'text-utilities', { defaultTool: 'random-word' });
addTool('random-email-address-generator', 'Random Email Generator', 'Placeholder email addresses for professional testing.', 'Security', [], (i: any) => '', 'Email', 'text', 'text-utilities', { defaultTool: 'random-email' });
addTool('ascii-art-generator', 'ASCII Art Generator', 'Convert text to ASCII banners.', 'Content', [
    { id: 'text', label: 'Input Text', type: 'text', placeholder: 'Enter word or phrase (A-Z, 0-9)' }
], (i: any) => {
    const text = (i.text || '').toUpperCase();
    const font: Record<string, string[]> = {
        'A': ['  ██  ', ' ████ ', '██  ██', '██████', '██  ██'],
        'B': ['█████ ', '██  ██', '█████ ', '██  ██', '█████ '],
        'C': [' ████ ', '██  ██', '██    ', '██  ██', ' ████ '],
        'D': ['████  ', '██  ██', '██  ██', '██  ██', '████  '],
        'E': ['██████', '██    ', '████  ', '██    ', '██████'],
        'F': ['██████', '██    ', '████  ', '██    ', '██    '],
        'G': [' ████ ', '██    ', '██ ███', '██  ██', ' ████ '],
        'H': ['██  ██', '██  ██', '██████', '██  ██', '██  ██'],
        'I': ['██████', '  ██  ', '  ██  ', '  ██  ', '██████'],
        'J': ['  ████', '    ██', '    ██', '██  ██', ' ████ '],
        'K': ['██  ██', '██ ██ ', '████  ', '██ ██ ', '██  ██'],
        'L': ['██    ', '██    ', '██    ', '██    ', '██████'],
        'M': ['██  ██', '██████', '██████', '██  ██', '██  ██'],
        'N': ['██  ██', '███ ██', '██████', '██ ███', '██  ██'],
        'O': [' ████ ', '██  ██', '██  ██', '██  ██', ' ████ '],
        'P': ['█████ ', '██  ██', '█████ ', '██    ', '██    '],
        'Q': [' ████ ', '██  ██', '██  ██', ' █████', '     ██'],
        'R': ['█████ ', '██  ██', '█████ ', '██ ██ ', '██  ██'],
        'S': [' █████', '██    ', ' ████ ', '    ██', '█████ '],
        'T': ['██████', '  ██  ', '  ██  ', '  ██  ', '  ██  '],
        'U': ['██  ██', '██  ██', '██  ██', '██  ██', ' ████ '],
        'V': ['██  ██', '██  ██', '██  ██', ' ████ ', '  ██  '],
        'W': ['██  ██', '██  ██', '██████', '██████', '██  ██'],
        'X': ['██  ██', ' ████ ', '  ██  ', ' ████ ', '██  ██'],
        'Y': ['██  ██', ' ████ ', '  ██  ', '  ██  ', '  ██  '],
        'Z': ['██████', '   ██ ', '  ██  ', ' ██   ', '██████'],
        ' ': ['      ', '      ', '      ', '      ', '      '],
        '0': [' ████ ', '██  ██', '██  ██', '██  ██', ' ████ '],
        '1': ['  ██  ', ' ███  ', '  ██  ', '  ██  ', '██████'],
        '2': [' ████ ', '██  ██', '   ██ ', '  ██  ', '██████'],
        '3': ['██████', '   ██ ', ' ████ ', '   ██ ', '██████'],
        '4': ['██  ██', '██  ██', '██████', '    ██', '    ██'],
        '5': ['██████', '██    ', '█████ ', '    ██', '█████ '],
        '6': [' ████ ', '██    ', '█████ ', '██  ██', ' ████ '],
        '7': ['██████', '   ██ ', '  ██  ', ' ██   ', '██    '],
        '8': [' ████ ', '██  ██', ' ████ ', '██  ██', ' ████ '],
        '9': [' ████ ', '██  ██', ' █████', '    ██', ' ████ '],
        '.': ['      ', '      ', '      ', '  ██  ', '  ██  '],
        '!': ['  ██  ', '  ██  ', '  ██  ', '      ', '  ██  '],
        '?': [' ████ ', '██  ██', '  ███ ', '      ', '  ██  '],
    };
    const rows = ['', '', '', '', ''];
    for (let r = 0; r < 5; r++) {
        for (let c = 0; c < text.length; c++) {
            const char = text[c];
            const fontChar = font[char] || ['░░░░', '░░░░', '░░░░', '░░░░', '░░░░'];
            rows[r] += fontChar[r] + '  ';
        }
    }
    return rows.join('\n');
}, 'ASCII Art', 'code', 'generic');
addTool('random-string-generator', 'Random String Generator', 'Generate secure random text strings instantly.', 'Security', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'random-string' });
addTool('pick-random-item', 'Pick Random Item Tool', 'Pick one or more random items from a list instantly. Free, secure, unbiased random item picker and name selector.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'pick-random-item' }, ['picker', 'select', 'randomizer'], ['random name picker', 'random item selector', 'fair decision maker', 'pick from list']);

addTool('url-slug-generator', 'URL Slug Generator', 'Convert text titles into clean, SEO-friendly URL slug strings. This tool handles character normalization and lowercase formatting locally in your browser.', 'Web & SEO', [
    { id: 'text', label: 'Title', type: 'text' }
], (i: any) => i.text.toLowerCase().replace(/ /g, '-'), 'Slug', 'text', 'text-utilities', { defaultTool: 'slugify' });

addTool('email-extractor-tool', 'Email Extractor', 'Find and extract emails from any text instantly. Professional data sanitization utility.', 'Web & SEO', [
    { id: 'text', label: 'Input Text', type: 'textarea' }
], (i: any) => i.text, 'Emails', 'text', 'text-utilities', { defaultTool: 'email-extractor' });

addTool('url-extractor-tool', 'URL Extractor', 'Extract and list every URL from HTML markup or raw text blocks. Audit domain links and map site architectures locally inside your browser context with complete privacy.', 'Web & SEO', [
    { id: 'text', label: 'Input Text', type: 'textarea' }
], (i: any) => i.text, 'URLs', 'text', 'text-utilities', { defaultTool: 'url-extractor' });

/*
addTool('sort-text-online', 'Alphabetical List Sorter', 'Organize your lists instantly.', 'Content', [
    { id: 'text', label: 'List Content', type: 'textarea' }
], (i: any) => i.text, 'Sorted List', 'text', 'text-utilities', { defaultTool: 'sort' });
*/

addTool('alphabetical-list-sorter', 'Alphabetical List Sorter', 'High-performance alphabetical sorting utility for list management. Organize lists, keywords, or content blocks instantly. Our tool processes everything inside your browser to ensure 100% privacy and accurate sorting for multilingual text strings.', 'Content', [
    { id: 'text', label: 'List Content', type: 'textarea' }
], (i: any) => i.text, 'Sorted List', 'text', 'text-utilities', { defaultTool: 'sort' });

addTool('online-word-counter', 'Online Word Counter', 'Analyze text length and density.', 'Content', [
    { id: 'text', label: 'Text', type: 'textarea' }
], (i: any) => i.text, 'Word Count', 'text', 'text-utilities', { defaultTool: 'word-count' });

addTool('text-difference-checker', 'Diff Checker', 'Compare two text blocks.', 'Content', [
    { id: 't1', label: 'Text 1', type: 'textarea' },
    { id: 't2', label: 'Text 2', type: 'textarea' }
], (i: any) => i.t1, 'Diff', 'text', 'text-utilities', { defaultTool: 'diff' });

addTool('find-and-replace-text', 'Find & Replace Text', 'Bulk edit text.', 'Content', [
    { id: 'txt', label: 'Text', type: 'textarea' }
], (i: any) => i.txt, 'Result', 'text', 'text-utilities', { defaultTool: 'find-replace' });

addTool('text-formatter', 'Case Converter', 'Toggle upper/lower case.', 'Content', [
    { id: 'txt', label: 'Text', type: 'textarea' }
], (i: any) => i.txt, 'Result', 'text', 'text-formatter');

addTool('remove-duplicate-text-lines', 'Duplicate Remover', 'Strip redundant lines.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'duplicates' });

addTool('reverse-word-order', 'Reverse Words', 'Flip the order of words.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'reverse-words' });

addTool('remove-emojis-from-text', 'Emoji Remover', 'Strip all emojis.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'remove-emojis' });

addTool('strip-html-tags-online', 'Strip HTML Tags', 'Convert HTML to plain text.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'strip-html' });

addTool('add-line-breaks-to-text', 'Add Line Breaks', 'Insert new lines.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'add-line-breaks' });

addTool('remove-line-breaks-from-text', 'Remove Line Breaks', 'Join multiple lines.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'remove-line-breaks' });

addTool('concatenate-text-lines', 'Concatenate Text', 'Join lists with delimiters.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'concatenate' });

addTool('add-prefix-suffix-to-lines', 'Prefix & Suffix', 'Bulk prepend or append text.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'prefix-suffix' });

addTool('reverse-letters-in-text', 'Reverse Letters', 'Invert every character.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'reverse-letters' });

addTool('reverse-list-order', 'Reverse List Order', 'Flip the order of items.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'reverse-list' });

addTool('swap-columns-in-text', 'Swap Columns', 'Exchange positions of columns.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'swap-columns' });

addTool('math-solver', 'Math Solver', 'Solve equations.', 'Developer', [
    { id: 'exp', label: 'Equation', type: 'text' }
], (i: any) => `x = 5`, 'Solution', 'text', 'math-solver');

addTool('text-to-quiz', 'Text to Quiz', 'Generate quizzes.', 'Developer', [
    { id: 'txt', label: 'Text', type: 'textarea' }
], (i: any) => `Q1: ...`, 'Quiz', 'text', 'text-to-quiz');

addTool('mutual-fund-calculator', 'Mutual Fund Calculator', 'Estimate your mutual fund returns over time. Enter your monthly investment amount, expected growth rate, and duration to see potential returns using compound growth logic, processed entirely in your browser.', 'Financial', [], (i: any) => '', 'Result', 'text', 'mutual-fund-calc');

addTool('fuel-cost-calculator', 'Fuel Cost Calculator', 'Professional logistics and trip expense modeling utility. Calculate precise fuel consumption and financial overhead for commercial or personal travel. Account for multi-variable inputs including fuel price, vehicle efficiency, and total distance to optimize your transportation budget.', 'Financial', [], (i: any) => '', 'Result', 'text', 'fuel-calc');

addTool('pincode-finder', 'Pincode Finder', 'Search and verify postal codes across states and union territories in India. Get instant access to regional administrative data, processed locally in your browser for absolute privacy.', 'Financial', [], (i: any) => '', 'Result', 'text', 'pincode-finder');

addTool('ifsc-checker', 'IFSC Checker', 'Verify bank branch details for secure NEFT, RTGS, and IMPS transactions. Confirm bank names, branch addresses, and validity offline in your browser window.', 'Financial', [], (i: any) => '', 'Result', 'text', 'ifsc-checker');

addTool('invoice-generator', 'Free Invoice Generator', 'Generate client invoices and receipts. Create billing documents with customizable itemization, tax percentages, and totals locally without subscription requirements.', 'Financial', [], (i: any) => '', 'Result', 'text', 'invoice');

addTool('expense-tracker-tool', 'Free Expense Tracker Online', 'Track your business spending (Private & Fast).', 'Financial', [], (i: any) => '', 'Result', 'text', 'expense-tracker');

// Batch 7: AI & Business
// Redundant entries removed

// Handled by component

addTool('mission-vision-generator', 'Free Mission & Vision Generator', 'Generate company mission and vision statements.', 'AI & Marketing', [
    { id: 'company', label: 'Company Name', type: 'text' }
], (i: any) => `Mission: To serve ${i.company}...`, 'Statement', 'text', 'mission-vision');

addTool('press-release-generator', 'Free Press Release Generator Online', 'Create professional press releases instantly.', 'AI & Marketing', [
    { id: 'subject', label: 'Announcement Subject', type: 'text', placeholder: 'e.g. Launch of Next-Gen AI Suite', required: true, minLength: 5, maxLength: 100 },
    { id: 'details', label: 'Key Details / Facts', type: 'textarea', placeholder: 'Enter key announcement details...', required: true, minLength: 20, maxLength: 1000 }
], (i: any) => `FOR IMMEDIATE RELEASE: ...`, 'Press Release', 'text', 'press-release');

addTool('customer-survey-generator', 'Free Customer Survey Generator', 'Create customer feedback surveys online.', 'AI & Marketing', [
    { id: 'goal', label: 'Survey Goal', type: 'text' }
], (i: any) => `Q1: How do you feel about ${i.goal}?`, 'Survey Questions', 'text', 'survey');

addTool('ai-prompt-generator', 'Free AI Prompt Generator Online', 'Optimize your AI prompts for better results.', 'AI & Marketing', [
    { id: 'topic', label: 'Topic', type: 'text' }
], (i: any) => i.topic, 'Result', 'text', 'ai-prompt-generator');

addTool('business-quotation-generator', 'Business Quotation Maker', 'Generate quotation sheets and project estimates for your clients. Enter itemized details, discounts, and tax rates to build structured, clean financial quotes offline.', 'Financial', [], (i: any) => '', 'Result', 'text', 'business-generator', { type: 'quotation' });

addTool('proforma-invoice-generator', 'Proforma Invoice Generator', 'Create proforma invoices to send to customers before delivering products or services. Input billing details and tax rates to generate documents locally without remote storage.', 'Financial', [], (i: any) => '', 'Result', 'text', 'business-generator', { type: 'invoice' });

addTool('business-bill-generator', 'Business Bill Generator', 'Create invoices and transaction receipts. Set tax percentages, custom descriptions, and pricing details to output standard, readable bills locally.', 'Financial', [], (i: any) => '', 'Result', 'text', 'business-generator', { type: 'bill' });

// SEO Specific Aliases and Trending Tools
addTool('reading-time-calculator', 'Reading Time Calculator', 'Estimate reading time for any text block. Calculate how long it takes to read based on average reading speeds, helping you optimize blog posts and editorial layouts.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'word-count' });

/*
addTool('remove-duplicatelines-free', 'Remove Duplicate Lines', 'Strip duplicate lines from lists and text blocks. Clean up keyword databases, logs, or lists instantly in your browser with complete data privacy.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'duplicates' });

addTool('secure-password-vault-gen', 'Secure Password Generator', 'Generate strong random passwords offline. Select your desired complexity rules to output secure keys that remain strictly within your browser memory.', 'Security', [], (i: any) => '', 'Result', 'text', 'password');

addTool('json-beautifier-online', 'JSON Beautifier Online', 'Format and beautify minified JSON blocks. Convert dense JSON arrays into readable, indented trees while validating syntax locally without sending data online.', 'Developer', [], (i: any) => '', 'Result', 'text', 'json');

addTool('alphabetical-keyword-sorter', 'Keyword Sorting Tool', 'Sort keyword lists alphabetically. Clean up search term spreadsheets and text documents locally to streamline content planning.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'sort' });

addTool('email-finder-from-text', 'Extract Emails from Text', 'Extract all valid email addresses from any unstructured text block. Copy and paste your data to pull out email addresses safely in your browser.', 'Web & SEO', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'email-extractor' });

addTool('html-to-text-converter', 'HTML to Text Converter', 'Convert HTML code into clean plain text. Strip script tags, styling, and markup elements to extract text content locally.', 'Content', [], (i: any) => '', 'Result', 'text', 'text-utilities', { defaultTool: 'strip-html' });
*/

addTool('ai-content-detector', 'AI Content Detector', 'Professional-grade AI content analysis and authenticity checker. Evaluate text patterns, perplexity, and burstiness to detect ChatGPT, Claude, and Gemini outputs with high precision.', 'AI & Marketing', [], (i: any) => '', 'Result', 'text', 'ai-detector', {}, ['detect', 'analyze', 'evaluate', 'chatgpt detector', 'ai writing checker', 'detect ai content', 'zerogpt free', 'bypass ai detector', 'content authenticity', 'ai essay checker']);

addTool('ai-text-humanizer', 'AI Text Humanizer', 'Convert AI-generated text into human-like writing. Optimize sentence structure, vocabulary variation, and readability flags to bypass AI detection filters seamlessly.', 'AI & Marketing', [], (i: any) => '', 'Result', 'text', 'ai-humanizer', {}, ['humanize', 'transform', 'rewrite', 'ai text bypasser', 'make ai text human', 'undetectable ai free', 'humanize chatgpt text', 'remove ai footprint', 'ai rewriter free']);

addTool('ai-image-generator', 'AI Image Generator', 'Create beautiful, high-quality images from text descriptions instantly. Uses advanced text-to-image AI diffusion models with customizable settings and an integration API.', 'AI & Marketing', [], (i: any) => '', 'Result', 'text', 'ai-image-generator', {}, ['generate', 'create', 'synthesize', 'draw', 'paint', 'banana image api', 'text to image free', 'ai photo generator'], ['text to image', 'ai picture generator', 'image creator online', 'free ai art generator']);

// Override metadata specifically for SEO targeting on requested high-volume keywords
if (toolRegistry['ai-content-detector']) {
    toolRegistry['ai-content-detector'].metadata = {
        ...toolRegistry['ai-content-detector'].metadata,
        title: 'AI Content Detector – Free OpenAI, ChatGPT & GPT Detector',
        description: 'Professional-grade AI content analysis and authenticity checker. Evaluate text patterns, perplexity, and burstiness to detect ChatGPT, Claude, and Gemini outputs with high precision.',
        keywords: [
            'open ai detector',
            'open ai detection tool',
            'gpt ai detector',
            'gpt detector',
            'openai detector',
            'chat gpt detector',
            'ai content detector',
            'free ai content detector',
            'ai text detector',
            'gpt 2 ai detector',
            'chat gpt output detector',
            'ai detection tool',
            'chat gpt 3 detector',
            'chat gpt 5 detector',
            'ai output detector',
            'hugging face ai detector',
            'ai generated text detector',
            'gpt 3 detector',
            'huggingface openai detector',
            'ai detector essay',
            'gptzero',
            'zerogpt',
            'detect chatgpt text',
            'gpt zero',
            'zero gpt',
            'api'
        ]
    };
}

if (toolRegistry['gst-calculator-india']) {
    toolRegistry['gst-calculator-india'].metadata = {
        ...toolRegistry['gst-calculator-india'].metadata,
        title: 'GST Calculator India – Free Online GST Tax Calculator',
        description: 'Calculate Goods and Services Tax (GST) in India with our free online GST calculator. Easily compute CGST, SGST, and IGST components for inclusive or exclusive rates.',
        keywords: [
            'gst calculator',
            'tax calculator',
            'gst tax calculator',
            'online gst calculator',
            'gst calculator online',
            'free gst calculator',
            'gst calculator india',
            'calculate gst india',
            'gst calc',
            'indian gst tax calculator',
            'daily utility tools'
        ]
    };
}

if (toolRegistry['profit-margin-calculator']) {
    toolRegistry['profit-margin-calculator'].metadata = {
        ...toolRegistry['profit-margin-calculator'].metadata,
        title: 'Profit Margin Calculator – Free Stock & Business Profit Margin Calculator',
        description: 'Analyze business margins and stock profits with our free online profit margin calculator. Calculate gross profit, cost of goods sold, and markups instantly.',
        keywords: [
            'profit margin calculator',
            'stock profit calculator',
            'profit calculator',
            'business profit margin calculator',
            'gross profit margin calculator',
            'markup calculator',
            'calculate profit margin',
            'daily utility tools'
        ]
    };
}

if (toolRegistry['pdf-tools']) {
    toolRegistry['pdf-tools'].metadata = {
        ...toolRegistry['pdf-tools'].metadata,
        title: 'PDF Management Suite – Free Online PDF Tools',
        description: 'Industrial-grade PDF manipulation tools. Merge PDF, split PDF, compress PDF, convert PDF, extract pages, and create PDFs entirely in your browser safely.',
        keywords: [
            'pdf',
            'print pdf',
            'create pdf',
            'view pdf',
            'convert pdf',
            'manipulate pdf',
            'ocr pdf',
            'scan to pdf',
            'annotate pdf',
            'markup pdf',
            'batch convert',
            'batch conversion',
            'free pdf tool',
            'pdf converter',
            'pdf converter online',
            'wps pdf tool',
            'free online tools',
            'online pdf tools'
        ]
    };
}

if (toolRegistry['merge-pdf']) {
    toolRegistry['merge-pdf'].metadata = {
        ...toolRegistry['merge-pdf'].metadata,
        title: 'Merge PDF Online Free – Combine PDF Files',
        description: 'Combine multiple PDF files into one document instantly. Our free online PDF merger works entirely in your browser – no file uploads, no sign-up required. Merge PDF files of any size securely and privately.',
        keywords: [
            'merge pdf',
            'merge pdf online',
            'combine pdf',
            'combine pdf files',
            'join pdf files free',
            'pdf merger',
            'free online pdf tools',
            'free pdf tool',
            'pdf converter',
            'pdf converter online',
            'document',
            'pdf file',
            'pdf document',
            'adobe',
            'acrobat reader',
            'adobe acrobat'
        ]
    };
}

if (toolRegistry['split-pdf']) {
    toolRegistry['split-pdf'].metadata = {
        ...toolRegistry['split-pdf'].metadata,
        title: 'Split PDF Online Free – Extract PDF Pages',
        description: 'Split a PDF into individual pages or extract specific page ranges instantly. Our free PDF splitter works 100% in your browser with no file uploads. Separate PDF pages in seconds.',
        keywords: [
            'split pdf',
            'split pdf online',
            'extract pdf',
            'split pdf pages',
            'pdf page extractor',
            'separate pdf pages',
            'pdf splitter free',
            'free online pdf tools',
            'free pdf tool',
            'pdf converter',
            'pdf converter online',
            'document',
            'pdf file',
            'pdf document',
            'adobe',
            'acrobat reader',
            'adobe acrobat'
        ]
    };
}

if (toolRegistry['compress-pdf']) {
    toolRegistry['compress-pdf'].metadata = {
        ...toolRegistry['compress-pdf'].metadata,
        title: 'Compress PDF Online Free – Reduce PDF Size',
        description: 'Reduce PDF file size instantly without losing quality. Our free PDF compressor works entirely in your browser – no uploads, no account needed. Shrink PDF files for email and web sharing.',
        keywords: [
            'compress pdf',
            'compress pdf online',
            'reduce pdf size',
            'compress pdf file',
            'pdf size reducer',
            'pdf compressor',
            'free online pdf tools',
            'free pdf tool',
            'pdf converter',
            'pdf converter online'
        ]
    };
}

if (toolRegistry['pdf-to-jpg']) {
    toolRegistry['pdf-to-jpg'].metadata = {
        ...toolRegistry['pdf-to-jpg'].metadata,
        title: 'PDF to JPG Converter Online Free – Extract Images',
        description: 'Convert PDF pages to high-quality JPG images instantly. Free online PDF to image converter that works in your browser with no file uploads. Extract images from PDF files securely.',
        keywords: [
            'pdf to jpg',
            'pdf to image',
            'pdf to jpeg',
            'convert pdf to jpg',
            'pdf image extractor',
            'pdf converter online',
            'free pdf tool'
        ]
    };
}

if (toolRegistry['jpg-to-pdf']) {
    toolRegistry['jpg-to-pdf'].metadata = {
        ...toolRegistry['jpg-to-pdf'].metadata,
        title: 'JPG to PDF Converter Online Free – Image to PDF',
        description: 'Convert JPG, PNG and image files to PDF instantly. Free online image to PDF converter – works in your browser, no uploads required. Create PDF from photos and images in seconds.',
        keywords: [
            'jpg to pdf',
            'image to pdf',
            'png to pdf',
            'convert jpg to pdf',
            'photo to pdf',
            'pdf converter online',
            'free pdf tool'
        ]
    };
}

if (toolRegistry['word-to-pdf']) {
    toolRegistry['word-to-pdf'].metadata = {
        ...toolRegistry['word-to-pdf'].metadata,
        title: 'Word to PDF Converter Online Free – DOC/DOCX to PDF',
        description: 'Convert Word documents (DOC, DOCX) to PDF instantly. Free online Word to PDF converter that runs in your browser with no file uploads. Create PDF from Word files securely.',
        keywords: [
            'word to pdf',
            'doc to pdf',
            'docx to pdf',
            'microsoft word to pdf',
            'word document to pdf',
            'convert word to pdf',
            'pdf converter online',
            'free pdf tool'
        ]
    };
}

if (toolRegistry['excel-to-pdf']) {
    toolRegistry['excel-to-pdf'].metadata = {
        ...toolRegistry['excel-to-pdf'].metadata,
        title: 'Excel to PDF Converter Online Free – Spreadsheets to PDF',
        description: 'Convert Excel spreadsheets (XLS, XLSX) to PDF instantly. Free online Excel to PDF converter that works in your browser with no uploads required. Turn spreadsheets into PDF files.',
        keywords: [
            'excel to pdf',
            'xls to pdf',
            'xlsx to pdf',
            'spreadsheet to pdf',
            'excel file to pdf',
            'convert excel to pdf',
            'pdf converter online',
            'free pdf tool'
        ]
    };
}

if (toolRegistry['powerpoint-to-pdf']) {
    toolRegistry['powerpoint-to-pdf'].metadata = {
        ...toolRegistry['powerpoint-to-pdf'].metadata,
        title: 'PowerPoint to PDF Converter Online Free – PPT to PDF',
        description: 'Convert PowerPoint presentations (PPT, PPTX) to PDF instantly. Free online PPT to PDF converter – no uploads, no account needed. Turn slides into PDF documents in seconds.',
        keywords: [
            'powerpoint to pdf',
            'ppt to pdf',
            'pptx to pdf',
            'presentation to pdf',
            'powerpoint converter',
            'convert ppt to pdf',
            'pdf converter online',
            'free pdf tool'
        ]
    };
}

if (toolRegistry['financial-tools']) {
    toolRegistry['financial-tools'].metadata = {
        ...toolRegistry['financial-tools'].metadata,
        title: 'Financial Analysis Suite – Free Online Financial Calculators',
        description: 'Access professional financial calculators online for free. Calculate GST, monthly EMI payments, annual income tax liabilities, percentage problems, profit margins, and more with secure browser-side processing.',
        keywords: [
            'financial tools',
            'financial calculators',
            'free business calculator',
            'finance tools online',
            'online calculators',
            'gst calculator',
            'emi calculator',
            'loan calculator',
            'income tax calculator',
            'percentage calculator',
            'profit margin calculator',
            'business calculators',
            'utility tools',
            'free online tools'
        ]
    };
}

if (toolRegistry['security-tools']) {
    toolRegistry['security-tools'].metadata = {
        ...toolRegistry['security-tools'].metadata,
        title: 'Security & Privacy Suite – Secure Encryption & Hashing Utilities',
        description: 'Run secure security and privacy utilities online. Generate random passwords, debug JSON Web Tokens (JWT), audit SQL queries, compute Bcrypt hashes, and generate random values completely offline in your browser.',
        keywords: [
            'security tools',
            'privacy tools',
            'secure password generator',
            'bcrypt hasher',
            'jwt decoder',
            'sql injection auditor',
            'crypto tools',
            'online security utility',
            'private online tools',
            'safe password generator',
            'free online tools'
        ]
    };
}

if (toolRegistry['developer-tools']) {
    toolRegistry['developer-tools'].metadata = {
        ...toolRegistry['developer-tools'].metadata,
        title: 'Developer & Data Suite – Free Online Programming & Conversion Tools',
        description: 'High-speed browser-native developer tools. Convert files between JSON, CSV, XML, and YAML, format JSON data, generate QR codes, audit web pages, and build code animations safely.',
        keywords: [
            'developer tools',
            'dev tools online',
            'json formatter',
            'yaml to json',
            'json to csv',
            'xml to json',
            'html to jsx',
            'scientific calculator',
            'math solver',
            'data converter online',
            'online web utilities',
            'free coding tools',
            'free online tools'
        ]
    };
}

if (toolRegistry['text-utilities']) {
    toolRegistry['text-utilities'].metadata = {
        ...toolRegistry['text-utilities'].metadata,
        title: 'Text & Content Utilities – Free Online Text Formatting Tools',
        description: 'Clean, format, and organize text documents instantly. Access alphabetical list sorters, word counters, difference checkers, find-and-replace editors, duplicate line removers, and HTML code strip utilities.',
        keywords: [
            'text utilities',
            'content tools',
            'word counter',
            'diff checker',
            'case converter',
            'alphabetical list sorter',
            'remove duplicate lines',
            'find and replace text',
            'reverse text',
            'remove emojis',
            'strip html tags',
            'free online tools'
        ]
    };
}

if (toolRegistry['ai-marketing-tools']) {
    toolRegistry['ai-marketing-tools'].metadata = {
        ...toolRegistry['ai-marketing-tools'].metadata,
        title: 'AI & Marketing Suite – Free Online Marketing Content Generators',
        description: 'Draft high-impact marketing materials and strategy guidelines with our client-side AI tools. Generate business plans, format professional social posts, analyze TikTok trends, suggest brand names, and optimize email subjects.',
        keywords: [
            'ai marketing tools',
            'marketing templates',
            'ai business plan generator',
            'linkedin post formatter',
            'youtube idea generator',
            'tiktok trend analyzer',
            'tagline generator',
            'brand name generator',
            'email subject optimizer',
            'sales script writer',
            'free online tools'
        ]
    };
}

if (toolRegistry['web-seo-tools']) {
    toolRegistry['web-seo-tools'].metadata = {
        ...toolRegistry['web-seo-tools'].metadata,
        title: 'Web & SEO Suite – Free Online Search Engine Optimization Utilities',
        description: 'Optimize site indexation and audit technical structure. Generate robots.txt rules, construct XML sitemaps, draft meta tag code, configure URL slugs, and extract link structures safely.',
        keywords: [
            'seo tools',
            'webmaster tools',
            'robots txt generator',
            'xml sitemap generator',
            'meta tag generator',
            'url slug generator',
            'email extractor',
            'url extractor',
            'online seo utility',
            'free webmaster tools',
            'free online tools'
        ]
    };
}



